import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import logo from '../assets/logo.png';
import '../styles/exam-management.css';
import ProfileDropdown from '../components/ProfileDropdown';
import CreateExamModal from '../components/CreateExamModal';
import API from '../api/api';
import Toast from '../components/Toast';
import { getStoredUserForRole } from '../utils/authStorage';


/* ─────────────────────────────────────────
   BODY ICONS
───────────────────────────────────────── */
const IconCalendar = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '4px', display: 'inline-block', verticalAlign: 'middle' }}>
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const IconMoreDots = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <circle cx="10" cy="10" r="1.25" fill="#9CA3AF" />
    <circle cx="5" cy="10" r="1.25" fill="#9CA3AF" />
    <circle cx="15" cy="10" r="1.25" fill="#9CA3AF" />
  </svg>
);

const parseSectionsConfig = (sectionsConfig) => {
  if (Array.isArray(sectionsConfig)) return sectionsConfig;
  if (typeof sectionsConfig === 'string') {
    try {
      const parsed = JSON.parse(sectionsConfig);
      return Array.isArray(parsed) ? parsed : [];
    } catch (err) {
      return [];
    }
  }
  return [];
};

const normalizeExamSection = (section = {}, index = 0) => {
  const isOptional = Boolean(section.isOptional ?? section.is_optional);
  const isCompulsory = Boolean(section.isCompulsory ?? section.is_compulsory ?? !isOptional);
  return {
    id: String(section.id || section.name || section.title || `section-${index + 1}`).trim().toLowerCase(),
    name: section.name || section.title || `Section ${index + 1}`,
    questions: Number(section.numQuestions ?? section.questions ?? 0),
    minutes: Number(section.timeMinutes ?? section.minutes ?? 0),
    easy: Number(section.easy ?? section.easy_count ?? 0),
    medium: Number(section.medium ?? section.medium_count ?? 0),
    difficult: Number(section.difficult ?? section.difficult_count ?? 0),
    isOptional,
    isCompulsory,
  };
};

/* ─────────────────────────────────────────
   EXAMS DATA & CARD COMPONENT
───────────────────────────────────────── */

const ExamCard = ({ exam, onEdit, onDelete, onUpdateStatus, onAssign, onPublishResult, onViewDetail, onDuplicate }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  const rawStatus = (exam.status || 'inactive').toLowerCase();
  const canViewDetail = !['inactive', 'draft'].includes(rawStatus);
  const statusConfig = {
    inactive: { label: 'DRAFT', className: 'draft' },
    draft: { label: 'DRAFT', className: 'draft' },
    active: { label: 'ACTIVE', className: 'active' },
    ongoing: { label: 'ONGOING', className: 'ongoing' },
    completed: { label: 'COMPLETED', className: 'completed' },
    results_published: { label: 'COMPLETED', className: 'completed' },
  }[rawStatus] || { label: 'DRAFT', className: 'draft' };
  const createdDate = exam.created_at
    ? new Date(exam.created_at).toISOString().slice(0, 10)
    : '';
  const sectionCount = exam.section_count != null ? String(exam.section_count) : '—';
  const closeDropdown = () => setShowDropdown(false);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const EditIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M8 2H3.33333C2.97971 2 2.64057 2.14048 2.39052 2.39052C2.14048 2.64057 2 2.97971 2 3.33333V12.6667C2 13.0203 2.14048 13.3594 2.39052 13.6095C2.64057 13.8595 2.97971 14 3.33333 14H12.6667C13.0203 14 13.3594 13.8595 13.6095 13.6095C13.8595 13.3594 14 13.0203 14 12.6667V8" stroke="#1A1D2E" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M12.2499 1.75015C12.5151 1.48493 12.8748 1.33594 13.2499 1.33594C13.625 1.33594 13.9847 1.48493 14.2499 1.75015C14.5151 2.01537 14.6641 2.37508 14.6641 2.75015C14.6641 3.12522 14.5151 3.48493 14.2499 3.75015L8.24123 9.75948C8.08293 9.91765 7.88737 10.0334 7.67257 10.0962L5.75723 10.6562C5.69987 10.6729 5.63906 10.6739 5.58117 10.6591C5.52329 10.6442 5.47045 10.6141 5.4282 10.5719C5.38594 10.5296 5.35583 10.4768 5.341 10.4189C5.32617 10.361 5.32717 10.3002 5.3439 10.2428L5.9039 8.32748C5.96692 8.11285 6.08292 7.91752 6.24123 7.75948L12.2499 1.75015Z" stroke="#1A1D2E" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  const DeleteIcon = () => (
    <svg width="15" height="16" viewBox="0 0 15 16" fill="none">
      <path d="M13.4904 2.34615H10.5577V1.75962C10.5577 1.29294 10.3723 0.845371 10.0423 0.515379C9.71232 0.185388 9.26476 0 8.79808 0H5.27885C4.81217 0 4.3646 0.185388 4.03461 0.515379C3.70462 0.845371 3.51923 1.29294 3.51923 1.75962V2.34615H0.586538C0.430979 2.34615 0.281791 2.40795 0.171793 2.51795C0.061796 2.62794 0 2.77713 0 2.93269C0 3.08825 0.061796 3.23744 0.171793 3.34744C0.281791 3.45743 0.430979 3.51923 0.586538 3.51923H1.17308V14.0769C1.17308 14.388 1.29667 14.6864 1.51666 14.9064C1.73666 15.1264 2.03503 15.25 2.34615 15.25H11.7308C12.0419 15.25 12.3403 15.1264 12.5603 14.9064C12.7803 14.6864 12.9038 14.388 12.9038 14.0769V3.51923H13.4904C13.6459 3.51923 13.7951 3.45743 13.9051 3.34744C14.0151 3.23744 14.0769 3.08825 14.0769 2.93269C14.0769 2.77713 14.0151 2.62794 13.9051 2.51795C13.7951 2.40795 13.6459 2.34615 13.4904 2.34615ZM4.69231 1.75962C4.69231 1.60406 4.7541 1.45487 4.8641 1.34487C4.9741 1.23487 5.12329 1.17308 5.27885 1.17308H8.79808C8.95364 1.17308 9.10283 1.23487 9.21282 1.34487C9.32282 1.45487 9.38462 1.60406 9.38462 1.75962V2.34615H4.69231V1.75962ZM11.7308 14.0769H2.34615V3.51923H11.7308V14.0769ZM5.86539 6.45192V11.1442C5.86539 11.2998 5.80359 11.449 5.69359 11.559C5.58359 11.669 5.43441 11.7308 5.27885 11.7308C5.12329 11.7308 4.9741 11.669 4.8641 11.559C4.7541 11.449 4.69231 11.2998 4.69231 11.1442V6.45192C4.69231 6.29636 4.7541 6.14717 4.8641 6.03718C4.9741 5.92718 5.12329 5.86538 5.27885 5.86538C5.43441 5.86538 5.58359 5.92718 5.69359 6.03718C5.80359 6.14717 5.86539 6.29636 5.86539 6.45192ZM9.38462 6.45192V11.1442C9.38462 11.2998 9.32282 11.449 9.21282 11.559C9.10283 11.669 8.95364 11.7308 8.79808 11.7308C8.64252 11.7308 8.49333 11.669 8.38333 11.559C8.27333 11.449 8.21154 11.2998 8.21154 11.1442V6.45192C8.21154 6.29636 8.27333 6.14717 8.38333 6.03718C8.49333 5.92718 8.64252 5.86538 8.79808 5.86538C8.95364 5.86538 9.10283 5.92718 9.21282 6.03718C9.32282 6.14717 9.38462 6.29636 9.38462 6.45192Z" fill="#1A1D2E" />
    </svg>
  );

  const ActivateIcon = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M10.0001 1.33301H4.00008C3.64646 1.33301 3.30732 1.47348 3.05727 1.72353C2.80722 1.97358 2.66675 2.31272 2.66675 2.66634V13.333C2.66675 13.6866 2.80722 14.0258 3.05727 14.2758C3.30732 14.5259 3.64646 14.6663 4.00008 14.6663H12.0001C12.3537 14.6663 12.6928 14.5259 12.9429 14.2758C13.1929 14.0258 13.3334 13.6866 13.3334 13.333V4.66634L10.0001 1.33301Z" stroke="#059669" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M9.33325 1.33301V3.99967C9.33325 4.3533 9.47373 4.69244 9.72378 4.94248C9.97383 5.19253 10.313 5.33301 10.6666 5.33301H13.3333" stroke="#059669" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M6.66659 6H5.33325" stroke="#059669" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M10.6666 8.66699H5.33325" stroke="#059669" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M10.6666 11.333H5.33325" stroke="#059669" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  const renderMenuItems = () => {
    if (rawStatus === 'completed') {
      return (
        <>
          <button className="em-card-dropdown-item" onClick={() => { onDuplicate(exam); closeDropdown(); }}><EditIcon />Duplicate Exam</button>
          <button className="em-card-dropdown-item em-card-dropdown-item--delete" onClick={() => { onDelete(exam.id); closeDropdown(); }}><DeleteIcon />Delete Exam</button>
        </>
      );
    }
    if (rawStatus === 'results_published') {
      return (
        <>
          <button className="em-card-dropdown-item" onClick={() => { onDuplicate(exam); closeDropdown(); }}><EditIcon />Duplicate Exam</button>
          <button className="em-card-dropdown-item em-card-dropdown-item--delete" onClick={() => { onDelete(exam.id); closeDropdown(); }}><DeleteIcon />Delete Exam</button>
        </>
      );
    }
    if (rawStatus === 'active') {
      return (
        <>
          <button className="em-card-dropdown-item" onClick={() => { onEdit(exam); closeDropdown(); }}><EditIcon />Edit Exam</button>
          <button className="em-card-dropdown-item em-card-dropdown-item--delete" onClick={() => { onDelete(exam.id); closeDropdown(); }}><DeleteIcon />Delete Exam</button>
        </>
      );
    }
    if (rawStatus === 'ongoing') {
      return (
        <>
          <button className="em-card-dropdown-item em-card-dropdown-item--delete" onClick={() => { onDelete(exam.id); closeDropdown(); }}><DeleteIcon />Delete Exam</button>
        </>
      );
    }
    return (
      <>
        <button className="em-card-dropdown-item" onClick={() => { onEdit(exam); closeDropdown(); }}><EditIcon />Edit Exam</button>
        <button className="em-card-dropdown-item em-card-dropdown-item--delete" onClick={() => { onDelete(exam.id); closeDropdown(); }}><DeleteIcon />Delete Exam</button>
        <button className="em-card-dropdown-item em-card-dropdown-item--green" onClick={() => { onUpdateStatus(exam.id, 'active'); closeDropdown(); }}><ActivateIcon />Activate Exam</button>
      </>
    );
  };

  return (
    <div
      className="em-card"
      onClick={() => canViewDetail && onViewDetail && onViewDetail(exam)}
      style={{ cursor: canViewDetail ? 'pointer' : 'default' }}
    >
      <div className={`em-card-accent em-card-accent--${statusConfig.className}`} />
      <div className="em-card-top">
        <div className="em-card-top-left">
          <span className={`em-card-badge em-card-badge--${statusConfig.className}`}>
            {statusConfig.label}
          </span>
          <span className="em-card-date" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            <IconCalendar />
            {createdDate}
          </span>
        </div>
        <span className="em-card-section-count">{sectionCount} sections</span>
        <div className="em-card-more-wrap" ref={dropdownRef} onClick={e => e.stopPropagation()}>
          <button
            className="em-card-more"
            onClick={() => setShowDropdown(!showDropdown)}
            aria-label="Exam actions"
          >
            <IconMoreDots />
          </button>
          {showDropdown && (
            <div className="em-card-dropdown">
              {renderMenuItems()}
            </div>
          )}
        </div>
      </div>
      <div className="em-card-main-row">
        <div className="em-card-copy">
          <h3 className="em-card-title" title={exam.title}>{exam.title}</h3>
          <p className="em-card-desc" title={exam.description || '(no description)'}>{exam.description || '(no description)'}</p>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────
   SIDEBAR ICONS & HELPERS
───────────────────────────────────────── */

const UploadIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <path d="M9 12V4.5M9 4.5L6.25 7.25M9 4.5L11.75 7.25" stroke="#4D8BF1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M4 13.5H14" stroke="#4D8BF1" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const ChooseCandidateIcon = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip_choose_candidate)">
      <path d="M8.00633 10.5099V9.50885C8.00633 8.97789 7.7954 8.46866 7.41995 8.09321C7.0445 7.71776 6.53528 7.50684 6.00431 7.50684H3.00128C2.47032 7.50684 1.9611 7.71776 1.58564 8.09321C1.21019 8.46866 0.999268 8.97789 0.999268 9.50885V10.5099" stroke="#4683E5" strokeWidth="1.00101" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M4.50373 5.50501C5.60941 5.50501 6.50574 4.60868 6.50574 3.50299C6.50574 2.39731 5.60941 1.50098 4.50373 1.50098C3.39804 1.50098 2.50171 2.39731 2.50171 3.50299C2.50171 4.60868 3.39804 5.50501 4.50373 5.50501Z" stroke="#4683E5" strokeWidth="1.00101" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M9.50732 4.00391V7.00693" stroke="#4683E5" strokeWidth="1.00101" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M11.0101 5.50488H8.00708" stroke="#4683E5" strokeWidth="1.00101" strokeLinecap="round" strokeLinejoin="round" />
    </g>
    <defs>
      <clipPath id="clip_choose_candidate">
        <rect width="12.0121" height="12.0121" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

const TrophyIcon = () => (
  <svg width="100" height="100" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1.98145 49.7939C1.98145 23.3894 23.3865 1.98438 49.791 1.98438C76.1954 1.98438 97.6005 23.3894 97.6005 49.7939C97.6005 76.1984 76.1954 97.6034 49.791 97.6034C23.3865 97.6034 1.98145 76.1984 1.98145 49.7939Z" fill="url(#paint0_linear_3399_2453)" />
    <mask id="path-2-inside-1_3399_2453" fill="white">
      <path d="M63.0873 13.2938C83.2455 20.6359 93.6349 42.9293 86.2928 63.0875C78.9507 83.2456 56.6572 93.6351 36.4991 86.2929C16.3409 78.9508 5.95151 56.6574 13.2936 36.4992C20.6358 16.3411 42.9292 5.95163 63.0873 13.2938Z" />
    </mask>
    <path d="M13.2936 36.4992L14.3465 36.8827C14.5988 36.19 14.8693 35.5092 15.1571 34.8408L14.1279 34.3976L13.0987 33.9545C12.794 34.6622 12.5077 35.3827 12.2408 36.1157L13.2936 36.4992Z" fill="white" fillOpacity="0.5" mask="url(#path-2-inside-1_3399_2453)" />
    <mask id="path-4-inside-2_3399_2453" fill="white">
      <path d="M85.3432 54.4684C82.7617 74.1027 64.7521 87.9268 45.1178 85.3453C25.4834 82.7638 11.6593 64.7543 14.2408 45.1199C16.8223 25.4855 34.8319 11.6614 54.4662 14.243C74.1006 16.8245 87.9247 34.834 85.3432 54.4684Z" />
    </mask>
    <path d="M54.4662 14.243L54.3688 14.9836C54.6102 15.0153 54.8506 15.0494 55.0901 15.0859L55.2026 14.3474L55.315 13.6089C55.0655 13.5709 54.815 13.5354 54.5636 13.5023L54.4662 14.243Z" fill="white" fillOpacity="0.3" mask="url(#path-4-inside-2_3399_2453)" />
    <g opacity="0.992635">
      <path d="M1.98145 49.7939C1.98145 23.3894 23.3865 1.98438 49.791 1.98438C76.1954 1.98438 97.6005 23.3894 97.6005 49.7939C97.6005 76.1984 76.1954 97.6034 49.791 97.6034C23.3865 97.6034 1.98145 76.1984 1.98145 49.7939Z" fill="url(#paint0_linear_3399_2453)" />
    </g>
    <g opacity="0.992635">
      <path d="M1.98145 49.7939C1.98145 23.3894 23.3865 1.98438 49.791 1.98438C76.1954 1.98438 97.6005 23.3894 97.6005 49.7939C97.6005 76.1984 76.1954 97.6034 49.791 97.6034C23.3865 97.6034 1.98145 76.1984 1.98145 49.7939Z" fill="url(#paint1_linear_3399_2453)" />
    </g>
    <g opacity="0.992635">
      <path d="M1.98145 49.7939C1.98145 23.3894 23.3865 1.98438 49.791 1.98438C76.1954 1.98438 97.6005 23.3894 97.6005 49.7939C97.6005 76.1984 76.1954 97.6034 49.791 97.6034C23.3865 97.6034 1.98145 76.1984 1.98145 49.7939Z" fill="url(#paint2_linear_3399_2453)" />
    </g>
    <path d="M16.9229 49.7948C16.9229 31.6418 31.6388 16.9258 49.7919 16.9258C67.945 16.9258 82.6609 31.6418 82.6609 49.7948C82.6609 67.9479 67.945 82.6639 49.7919 82.6639C31.6388 82.6639 16.9229 67.9479 16.9229 49.7948Z" fill="white" fillOpacity="0.2" />
    <path d="M67.2432 39.584H63.1182V36.834C63.1182 36.4693 62.9733 36.1196 62.7154 35.8617C62.4576 35.6039 62.1078 35.459 61.7432 35.459H36.9932C36.6285 35.459 36.2788 35.6039 36.0209 35.8617C35.763 36.1196 35.6182 36.4693 35.6182 36.834V39.584H31.4932C30.7638 39.584 30.0643 39.8737 29.5486 40.3894C29.0329 40.9052 28.7432 41.6046 28.7432 42.334V45.084C28.7432 46.9073 29.4675 48.656 30.7568 49.9453C31.3952 50.5837 32.1531 51.0902 32.9872 51.4357C33.8213 51.7812 34.7153 51.959 35.6182 51.959H36.2455C37.0555 54.5258 38.5998 56.799 40.6876 58.4977C42.7755 60.1964 45.3153 61.246 47.9932 61.517V65.709H43.8682C43.5035 65.709 43.1538 65.8538 42.8959 66.1117C42.638 66.3696 42.4932 66.7193 42.4932 67.084C42.4932 67.4487 42.638 67.7984 42.8959 68.0563C43.1538 68.3141 43.5035 68.459 43.8682 68.459H54.8682C55.2328 68.459 55.5826 68.3141 55.8404 68.0563C56.0983 67.7984 56.2432 67.4487 56.2432 67.084C56.2432 66.7193 56.0983 66.3696 55.8404 66.1117C55.5826 65.8538 55.2328 65.709 54.8682 65.709H50.7432V61.5118C56.2329 60.9566 60.7875 57.1049 62.4444 51.959H63.1182C64.9415 51.959 66.6902 51.2347 67.9795 49.9453C69.2688 48.656 69.9932 46.9073 69.9932 45.084V42.334C69.9932 41.6046 69.7034 40.9052 69.1877 40.3894C68.672 39.8737 67.9725 39.584 67.2432 39.584ZM35.6182 49.209C34.5241 49.209 33.4749 48.7744 32.7013 48.0008C31.9278 47.2272 31.4932 46.178 31.4932 45.084V42.334H35.6182V47.834C35.6182 48.2923 35.6405 48.7507 35.6852 49.209H35.6182ZM60.3682 47.6793C60.3682 53.7843 55.3838 58.7893 49.3682 58.834C46.4508 58.834 43.6529 57.6751 41.59 55.6122C39.5271 53.5493 38.3682 50.7514 38.3682 47.834V38.209H60.3682V47.6793ZM67.2432 45.084C67.2432 46.178 66.8086 47.2272 66.035 48.0008C65.2614 48.7744 64.2122 49.209 63.1182 49.209H63.0322C63.0886 48.701 63.1173 48.1904 63.1182 47.6793V42.334H67.2432V45.084Z" fill="white" />
    <defs>
      <linearGradient id="paint0_linear_3399_2453" x1="1.98145" y1="1.98438" x2="97.6005" y2="97.6034" gradientUnits="userSpaceOnUse">
        <stop stopColor="#4D8BF1" />
        <stop offset="0.5" stopColor="#4D8BF1" />
        <stop offset="1" stopColor="#4D8BF1" />
      </linearGradient>
      <linearGradient id="paint1_linear_3399_2453" x1="1.98145" y1="97.6034" x2="97.6005" y2="1.98438" gradientUnits="userSpaceOnUse">
        <stop stopOpacity="0" />
        <stop offset="0.5" stopColor="white" stopOpacity="0.3" />
        <stop offset="1" stopOpacity="0" />
      </linearGradient>
      <linearGradient id="paint2_linear_3399_2453" x1="1.98145" y1="97.6034" x2="97.6005" y2="1.98438" gradientUnits="userSpaceOnUse">
        <stop stopOpacity="0" />
        <stop offset="0.5" stopColor="white" stopOpacity="0.3" />
        <stop offset="1" stopOpacity="0" />
      </linearGradient>
    </defs>
  </svg>
);

const ModalCloseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M11 3L3 11M3 3L11 11" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
  </svg>
);

const getCandidateInitials = (candidate) => {
  const sourceName = getCandidateDisplayName(candidate);

  return sourceName
    .split(/\s+/)
    .filter(Boolean)
    .map(part => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || '?';
};

const getCandidateDisplayName = (candidate) => {
  const name = String(candidate?.name || '').trim();
  if (name) return name;

  const firstName = String(candidate?.firstName || candidate?.first_name || '').trim();
  const lastName = String(candidate?.lastName || candidate?.last_name || '').trim();
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
  if (fullName) return fullName;

  const email = String(candidate?.email || '').trim();
  return email ? email.split('@')[0] : 'Unknown Candidate';
};

const getCandidateScore = (candidate) => {
  const possibleScore = [
    candidate?.percentage,
    candidate?.score_percentage,
    candidate?.result_percentage,
    candidate?.overall_percentage,
    candidate?.score,
  ].find(value => value !== undefined && value !== null && value !== '');

  const numericScore = Number(possibleScore);
  return Number.isFinite(numericScore) ? Math.max(0, Math.min(100, numericScore)) : null;
};

const PublishResultModal = ({ exam, onClose }) => {
  const [tab, setTab] = useState('overall');
  const [publishCandidates, setPublishCandidates] = useState([]);
  const [publishCandidatesLoading, setPublishCandidatesLoading] = useState(true);
  const sections = parseSectionsConfig(exam?.sections_config);
  const [sectionFilter, setSectionFilter] = useState(sections[0]?.name || '');
  const [topCandidateLimit, setTopCandidateLimit] = useState('');
  const [candidateView, setCandidateView] = useState('regular');
  const [sectionDropdownOpen, setSectionDropdownOpen] = useState(false);

  const [publishing, setPublishing] = useState(false);

  useEffect(() => {
    if (!exam?.id) return undefined;

    let isMounted = true;
    setPublishCandidatesLoading(true);

    API.get(`/exam/${exam.id}/candidates`)
      .then((res) => {
        if (!isMounted) return;
        setPublishCandidates(Array.isArray(res.data?.candidates) ? res.data.candidates : []);
      })
      .catch(() => {
        if (!isMounted) return;
        setPublishCandidates([]);
      })
      .finally(() => {
        if (isMounted) setPublishCandidatesLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [exam?.id]);

  // DEV MODE: simulate publish without API call
  const handlePublish = async () => {
  if (!canPublishResults) return;
  try {
    setPublishing(true);
    await API.post(`/exam/${exam.id}/publish-results`, {
      passedCandidateIds: passedPublishCandidates.map(candidate => candidate.id),
      regularCandidateIds: regularPublishCandidates.map(candidate => candidate.id),
      defaulterCandidateIds: defaulterPublishCandidates.map(candidate => candidate.id),
    });
    onClose(true);
  } catch (err) {
    console.error('Failed to publish results:', err);
    alert('Failed to publish results: ' + (err.response?.data?.message || err.message));
  } finally {
    setPublishing(false);
  }
};

  const getSectionScore = (candidate, sectionName) => {
    const raw = candidate.section_scores || candidate.sectionScores || candidate.sections;
    if (!sectionName || !raw) return getCandidateScore(candidate);
    if (Array.isArray(raw)) {
      const matched = raw.find(item => String(item.name || item.section_name || '').toLowerCase() === sectionName.toLowerCase());
      return getCandidateScore(matched || {});
    }
    return getCandidateScore({ percentage: raw[sectionName], score: raw[sectionName] });
  };

  const normalizeSectionName = (value) => String(value || '').trim().toLowerCase();
  const selectedSection = sections.find(section => normalizeSectionName(section.name || section.title) === normalizeSectionName(sectionFilter));
  const selectedSectionIsOptional = Boolean(selectedSection?.isOptional ?? selectedSection?.is_optional);

  const getSelectedOptionalSections = (candidate) => {
    const raw = candidate.selected_optional_sections || candidate.selectedOptionalSections || candidate.optional_sections_selected;
    if (Array.isArray(raw)) return raw.map(normalizeSectionName).filter(Boolean);
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed.map(normalizeSectionName).filter(Boolean);
      } catch (err) {
        return raw.split(',').map(normalizeSectionName).filter(Boolean);
      }
    }
    return [];
  };

  const hasNotAttemptedSelectedSection = (candidate) => {
    if (tab !== 'section' || !selectedSectionIsOptional || !sectionFilter) return false;
    return !getSelectedOptionalSections(candidate).includes(normalizeSectionName(sectionFilter));
  };

  if (!exam) return null;

  const scoredCandidates = publishCandidates
    .map((candidate) => {
      const score = getCandidateScore(candidate);
      const isDefaulter = candidate.submit_reason === 'tab_switch';
      return {
        ...candidate,
        _displayName: getCandidateDisplayName(candidate),
        _initials: getCandidateInitials(candidate),
        _score: score,
        _sectionScore: getSectionScore(candidate, sectionFilter),
        _sectionNotAttempted: hasNotAttemptedSelectedSection(candidate),
        _isDefaulter: isDefaulter,
      };
    })
    .filter(candidate => candidate._score !== null);

  const regularPublishCandidates = scoredCandidates.filter(candidate => !candidate._isDefaulter);
  const defaulterPublishCandidates = scoredCandidates.filter(candidate => candidate._isDefaulter);

  const rankedPublishCandidates = [...regularPublishCandidates].sort((a, b) => {
    const aScore = tab === 'section' ? (a._sectionNotAttempted ? -1 : (a._sectionScore ?? -1)) : (a._score ?? -1);
    const bScore = tab === 'section' ? (b._sectionNotAttempted ? -1 : (b._sectionScore ?? -1)) : (b._score ?? -1);
    return bScore - aScore;
  });
  const parsedTopCandidateLimit = Number.parseInt(topCandidateLimit, 10);
  const passedPublishCandidates = Number.isFinite(parsedTopCandidateLimit) && parsedTopCandidateLimit > 0
    ? rankedPublishCandidates.slice(0, parsedTopCandidateLimit)
    : [];
  const passedCandidateIdSet = new Set(passedPublishCandidates.map(candidate => candidate.id));
  const visiblePublishCandidates = candidateView === 'defaulters'
    ? defaulterPublishCandidates
    : rankedPublishCandidates;
  const visibleCountLabel = candidateView === 'defaulters'
    ? `${defaulterPublishCandidates.length} defaulter${defaulterPublishCandidates.length === 1 ? '' : 's'}`
    : `${regularPublishCandidates.length} regular`;
  const canPublishResults = topCandidateLimit.trim().length > 0 && parsedTopCandidateLimit > 0 && regularPublishCandidates.length > 0;

  return (
    <div className="emx-modal-overlay" onClick={() => onClose(false)}>
      <div className="emx-modal emx-publish-modal" onClick={e => e.stopPropagation()}>
        <button className="emx-modal-close" onClick={() => onClose(false)} aria-label="Close" disabled={publishing}>
          <ModalCloseIcon />
        </button>
        <div className="emx-publish-hero">
          <div className="emx-publish-hero-bg" />
          <div className="emx-trophy-ring">
            <TrophyIcon />
          </div>
        </div>
        <div className="emx-publish-title">
          <h2>Publish Exam Results</h2>
          <p>{exam.title}</p>
        </div>
        <div className="emx-mode-tabs emx-result-tabs">
          <button
            className={tab === 'overall' ? 'emx-mode-tab emx-mode-tab--active' : 'emx-mode-tab'}
            onClick={() => setTab('overall')}
            disabled={publishing}
          >
            Overall
          </button>
          <button
            className={tab === 'section' ? 'emx-mode-tab emx-mode-tab--active' : 'emx-mode-tab'}
            onClick={() => setTab('section')}
            disabled={publishing}
          >
            Section-wise
          </button>
        </div>
        <div className="emx-publish-toprow">
          <div className="emx-publish-candidates-header">
            <h3>
              Candidates
              <span>({publishCandidatesLoading ? '...' : visibleCountLabel})</span>
            </h3>
            <div className="emx-publish-header-controls">
              <div className="emx-defaulter-toggle" role="group" aria-label="Candidate list view">
                <button
                  type="button"
                  className={candidateView === 'regular' ? 'is-active' : ''}
                  onClick={() => setCandidateView('regular')}
                  disabled={publishing}
                >
                  Regular
                </button>
                <button
                  type="button"
                  className={candidateView === 'defaulters' ? 'is-active' : ''}
                  onClick={() => setCandidateView('defaulters')}
                  disabled={publishing}
                >
                  Defaulters
                </button>
              </div>
              <label className="emx-top-candidate-filter">
                <span>Top</span>
                <input
                  type="number"
                  min="1"
                  max={Math.max(1, regularPublishCandidates.length)}
                  value={topCandidateLimit}
                  onChange={e => setTopCandidateLimit(e.target.value.replace(/[^\d]/g, ''))}
                  placeholder="All"
                  disabled={publishing || candidateView === 'defaulters'}
                />
              </label>
              {tab === 'section' && sections.length > 0 && (
                <div className={sectionDropdownOpen ? 'emx-section-topper-select emx-section-topper-select--open' : 'emx-section-topper-select'}>
                  <button
                    type="button"
                    className="emx-section-topper-selected"
                    onClick={() => !publishing && setSectionDropdownOpen(open => !open)}
                    disabled={publishing}
                  >
                    <span>{sectionFilter || 'Select section'}</span>
                    <span className="emx-section-topper-caret">v</span>
                  </button>
                  {sectionDropdownOpen && (
                    <div className="emx-section-topper-menu">
                      {sections.map((section, index) => {
                        const name = section.name || `Section ${index + 1}`;
                        const selected = name === sectionFilter;
                        return (
                          <button
                            type="button"
                            key={name}
                            className={selected ? 'emx-section-topper-option is-selected' : 'emx-section-topper-option'}
                            onClick={() => {
                              setSectionFilter(name);
                              setSectionDropdownOpen(false);
                            }}
                          >
                            <span>{name}</span>
                            {selected && <span>&#10003;</span>}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="emx-modal-body emx-publish-body">
          <div className="emx-publish-content emx-publish-content--single">
            <div className="emx-publish-right emx-publish-right--full">
              <div className="emx-publish-candidates-panel">
                {publishCandidatesLoading ? (
                  <div className="emx-publish-empty">Loading candidates...</div>
                ) : visiblePublishCandidates.length === 0 ? (
                  <div className="emx-publish-empty">No assigned candidates found for this exam.</div>
                ) : (
                  <div className="emx-publish-candidate-list">
                    {visiblePublishCandidates.map((candidate, index) => {
                      const score = tab === 'section' ? candidate._sectionScore : candidate._score;
                      const sectionNotAttempted = tab === 'section' && candidate._sectionNotAttempted;
                      const resultLabel = candidate._isDefaulter
                        ? 'Defaulter'
                        : passedCandidateIdSet.has(candidate.id)
                          ? 'Pass'
                          : 'Fail';
                      return (
                        <div className="emx-publish-candidate-row" key={candidate.id || `${candidate.email}-${index}`}>
                          <span className="emx-publish-candidate-index">{index + 1}</span>
                          <span className="emx-publish-candidate-avatar">{getCandidateInitials(candidate)}</span>
                          <div className="emx-publish-candidate-copy">
                            <strong>{getCandidateDisplayName(candidate)}</strong>
                            <span>
                              {candidate.email || 'No email available'}
                              {candidate._isDefaulter ? ` · Tab switch ${candidate.tab_switch_count ? `(${candidate.tab_switch_count})` : ''}` : ''}
                            </span>
                          </div>
                          <span className={candidate._isDefaulter || sectionNotAttempted ? 'emx-publish-candidate-score is-not-attempted' : 'emx-publish-candidate-score'}>
                            {sectionNotAttempted ? 'Not Attempted' : (score !== null ? `${Math.round(score)}%` : '--')}
                          </span>
                          <span className={`emx-publish-result-pill ${resultLabel === 'Pass' ? 'is-pass' : resultLabel === 'Fail' ? 'is-fail' : 'is-defaulter'}`}>
                            {resultLabel}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="emx-modal-footer">
          <button className="emx-btn emx-btn--ghost" onClick={() => onClose(false)} disabled={publishing}>Cancel</button>
          <button className="emx-btn emx-btn--publish" onClick={handlePublish} disabled={publishing || !canPublishResults}>
            {publishing ? 'Publishing...' : 'Publish Results'}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────
   ASSIGN EXAM MODAL — Multi-step flow
───────────────────────────────────────── */
const AssignExamModal = ({ exam, onClose }) => {
  const [step, setStep] = useState('choice');
  const [candidates, setCandidates] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchRef = useRef(null);

  const [newCandidate, setNewCandidate] = useState({ firstName: '', lastName: '', email: '', mobileNo: '', resume: null, skill: '' });

  const [bulkFile, setBulkFile] = useState(null);
  const [bulkDrag, setBulkDrag] = useState(false);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [dueDate, setDueDate] = useState('');
  const [assignError, setAssignError] = useState('');
  const [toast, setToast] = useState(null);
  const bulkFileInputRef = useRef(null);
  const assignScrollViewportRef = useRef(null);
  const assignScrollContentRef = useRef(null);
  const assignScrollDragRef = useRef({ startY: 0, startScrollTop: 0 });
  const [assignScrollThumb, setAssignScrollThumb] = useState({ height: 0, top: 0, visible: false });
  const minDateTimeLocal = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16);

  const updateAssignScrollThumb = useCallback(() => {
    const viewport = assignScrollViewportRef.current;
    if (!viewport) return;

    const { clientHeight, scrollHeight, scrollTop } = viewport;
    if (scrollHeight <= clientHeight + 1) {
      setAssignScrollThumb(prev => (prev.visible ? { height: 0, top: 0, visible: false } : prev));
      return;
    }

    const thumbHeight = Math.max((clientHeight / scrollHeight) * clientHeight, 56);
    const maxThumbTop = Math.max(clientHeight - thumbHeight, 0);
    const maxScrollTop = Math.max(scrollHeight - clientHeight, 1);
    const thumbTop = (scrollTop / maxScrollTop) * maxThumbTop;

    setAssignScrollThumb(prev => {
      if (
        prev.visible &&
        Math.abs(prev.height - thumbHeight) < 1 &&
        Math.abs(prev.top - thumbTop) < 1
      ) {
        return prev;
      }
      return { height: thumbHeight, top: thumbTop, visible: true };
    });
  }, []);

  useEffect(() => {
    API.get('/users/candidates')
      .then(res => {
        const data = res.data;
        setCandidates(Array.isArray(data) ? data : Array.isArray(data?.candidates) ? data.candidates : []);
      })
      .catch(() => setCandidates([]));
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (step !== 'single') return undefined;

    const viewport = assignScrollViewportRef.current;
    const content = assignScrollContentRef.current;
    if (!viewport) return undefined;

    const refresh = () => {
      window.requestAnimationFrame(updateAssignScrollThumb);
    };

    refresh();
    window.addEventListener('resize', refresh);

    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(refresh);
      resizeObserver.observe(viewport);
      if (content) resizeObserver.observe(content);
    }

    return () => {
      window.removeEventListener('resize', refresh);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [step, updateAssignScrollThumb]);

  useEffect(() => {
    if (step !== 'single') return;
    const timer = window.setTimeout(updateAssignScrollThumb, 0);
    return () => window.clearTimeout(timer);
  }, [step, selectedCandidates, showDropdown, search, newCandidate, updateAssignScrollThumb]);

  useEffect(() => {
    return () => {
      document.body.style.userSelect = '';
    };
  }, []);

  const getRecentAttemptDate = (candidate) => {
    const attemptedAt = candidate?.recent_attempt_submitted_at || candidate?.submitted_at;
    if (!attemptedAt) return null;
    const attemptedDate = new Date(attemptedAt);
    if (Number.isNaN(attemptedDate.getTime())) return null;
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
    return attemptedDate >= sixMonthsAgo ? attemptedDate : null;
  };

  const getRecentAttemptMessage = (candidate) => {
    const attemptedDate = getRecentAttemptDate(candidate);
    const attemptedOn = attemptedDate ? ` on ${attemptedDate.toLocaleDateString('en-IN')}` : '';
    return `${candidate?.name || candidate?.email || 'This candidate'} has already attempted the exam in the past 6 months${attemptedOn}.`;
  };

  const showRecentAttemptPopup = (candidate) => {
    const message = getRecentAttemptMessage(candidate);
    setAssignError(message);
    setToast({ message, type: 'error' });
  };

  const handleSelectCandidate = (candidate) => {
    if (getRecentAttemptDate(candidate)) {
      showRecentAttemptPopup(candidate);
      setSearch('');
      setShowDropdown(false);
      return;
    }
    if (!selectedCandidates.some(c => c.id === candidate.id)) {
      setSelectedCandidates(prev => [...prev, candidate]);
    }
    setSearch('');
    setShowDropdown(false);
  };

  const handleRemoveCandidate = (id) => {
    setSelectedCandidates(prev => prev.filter(c => c.id !== id));
  };

  const handleAddNewCandidate = () => {
    const { firstName, lastName, email, skill } = newCandidate;
    if (!firstName.trim() || !lastName.trim() || !email.trim() || !newCandidate.mobileNo.trim() || !skill.trim()) {
      setAssignError('First name, last name, email, mobile no., and primary skill are required.');
      return;
    }
    const existingCandidate = candidates.find(c => (c.email || '').toLowerCase() === email.trim().toLowerCase());
    if (existingCandidate && getRecentAttemptDate(existingCandidate)) {
      showRecentAttemptPopup(existingCandidate);
      return;
    }
    const tempId = `new-${Date.now()}`;
    setSelectedCandidates(prev => [...prev, {
      id: tempId,
      name: `${firstName.trim()} ${lastName.trim()}`.trim(),
      email: email.trim(),
      mobileNo: newCandidate.mobileNo.trim(),
      skill: skill || '',
      resume: newCandidate.resume,
      isNew: true,
    }]);
    setNewCandidate({ firstName: '', lastName: '', email: '', mobileNo: '', resume: null, skill: '' });
  };

  const handleAssign = async () => {
    // Auto-include any unflushed new-candidate form data
    let allSelected = [...selectedCandidates];
    if (newCandidate.firstName.trim() && newCandidate.email.trim()) {
      if (!newCandidate.lastName.trim() || !newCandidate.mobileNo.trim() || !newCandidate.skill.trim()) {
        setAssignError('First name, last name, email, mobile no., and primary skill are required.');
        return;
      }
      const existingCandidate = candidates.find(c => (c.email || '').toLowerCase() === newCandidate.email.trim().toLowerCase());
      if (existingCandidate && getRecentAttemptDate(existingCandidate)) {
        showRecentAttemptPopup(existingCandidate);
        return;
      }
      const tempId = `new-${Date.now()}`;
      allSelected = [...allSelected, {
        id: tempId,
        name: `${newCandidate.firstName.trim()} ${newCandidate.lastName.trim()}`.trim(),
        email: newCandidate.email.trim(),
        skill: newCandidate.skill || '',
        mobileNo: newCandidate.mobileNo.trim(),
        isNew: true,
      }];
      setNewCandidate({ firstName: '', lastName: '', email: '', mobileNo: '', resume: null, skill: '' });
    }
    if (allSelected.length === 0) { setAssignError('Please select or add at least one candidate.'); return; }
    setLoading(true);
    setAssignError('');
    try {
      const existingIds = allSelected.filter(c => !c.isNew).map(c => c.id);
      const newCandidates = allSelected.filter(c => c.isNew).map(c => ({
        name: c.name, email: c.email, mobileNo: c.mobileNo || '', skill: c.skill || '',
      }));
      await API.post(`/exam/${exam.id}/assign`, {
        candidateIds: existingIds,
        newCandidates,
        dueDate: dueDate ? dueDate.slice(0, 10) : null,
        openAt: dueDate || null,
      });
      setLoading(false);
      setToast({ message: 'Exam assigned successfully! Credentials have been emailed to the candidates.', type: 'success' });
      onClose();
    } catch (err) {
      setLoading(false);
      const message = err.response?.data?.message || 'Assignment failed. Please try again.';
      setAssignError(message);
      if (err.response?.data?.blockedReason === 'recent_attempt' || message.toLowerCase().includes('past 6 months')) {
        setToast({ message, type: 'error' });
      }
    }
  };

  const handleBulkUpload = async () => {
    if (!bulkFile) return;
    setBulkUploading(true);
    setAssignError('');
    try {
      const formData = new FormData();
      formData.append('file', bulkFile);
      const res = await API.post(`/exam/${exam.id}/assign-bulk`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setBulkUploading(false);
      setToast({ message: res.data?.message || 'Bulk assignment successful!', type: 'success' });
      onClose();
    } catch (err) {
      setBulkUploading(false);
      const message = err.response?.data?.message || 'Bulk upload failed. Please try again.';
      setAssignError(message);
      if (err.response?.data?.blockedReason === 'recent_attempt' || message.toLowerCase().includes('past 6 months')) {
        setToast({ message, type: 'error' });
      }
    }
  };

  const handleDownloadTemplate = () => {
    const csv = 'first_name,last_name,email,mobile_no,college,degree,primary_skill\nJohn,Doe,john@example.com,9999999999,Sample College,B.Tech,Frontend\nJane,Smith,jane@example.com,8888888888,Sample University,MCA,Backend\n';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'candidate_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleClearBulkFile = () => {
    setBulkFile(null);
    if (bulkFileInputRef.current) {
      bulkFileInputRef.current.value = '';
    }
  };

  const filtered = candidates.filter(c => {
    const term = search.toLowerCase();
    return (c.name || '').toLowerCase().includes(term) || (c.email || '').toLowerCase().includes(term) || String(c.id).includes(term);
  });
  const hasCompleteNewCandidate =
    newCandidate.firstName.trim() &&
    newCandidate.lastName.trim() &&
    newCandidate.email.trim() &&
    newCandidate.mobileNo.trim() &&
    newCandidate.skill.trim();
  const canAssignCandidates = selectedCandidates.length > 0 || Boolean(hasCompleteNewCandidate);

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  };

  const handleAssignScrollDragStart = (event) => {
    const viewport = assignScrollViewportRef.current;
    if (!viewport || !assignScrollThumb.visible) return;

    event.preventDefault();
    assignScrollDragRef.current = {
      startY: event.clientY,
      startScrollTop: viewport.scrollTop,
    };

    const handleMouseMove = (moveEvent) => {
      const currentViewport = assignScrollViewportRef.current;
      if (!currentViewport) return;

      const { clientHeight, scrollHeight } = currentViewport;
      const maxScrollTop = Math.max(scrollHeight - clientHeight, 0);
      const maxThumbTravel = Math.max(clientHeight - assignScrollThumb.height, 1);
      const deltaY = moveEvent.clientY - assignScrollDragRef.current.startY;
      const scrollDelta = (deltaY / maxThumbTravel) * maxScrollTop;
      currentViewport.scrollTop = assignScrollDragRef.current.startScrollTop + scrollDelta;
      updateAssignScrollThumb();
    };

    const handleMouseUp = () => {
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const CloseBtn = () => (
    <button className="emx-modal-close" onClick={onClose} aria-label="Close">
      <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
        <path d="M10 1L1 10" stroke="currentColor" strokeWidth="1.84" strokeLinecap="round" />
        <path d="M1 1L10 10" stroke="currentColor" strokeWidth="1.84" strokeLinecap="round" />
      </svg>
    </button>
  );

  if (step === 'choice') {
    return (
      <div className="emx-modal-overlay" onClick={onClose}>
        <div className="emx-assign-choice" onClick={e => e.stopPropagation()}>
          <CloseBtn />
          <h2>Assign Candidates</h2>
          <p>Choose how you want to assign this exam to candidates</p>
          <div className="emx-choice-actions">
            <button onClick={() => setStep('single')}>
              <span className="emx-choice-icon-wrap">
                <ChooseCandidateIcon />
              </span>
              Choose Candidates
            </button>
            <button onClick={() => setStep('bulk')}>
              <span className="emx-choice-icon-wrap">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4D8BF1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
              </span>
              Bulk Upload
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'bulk') {
    return (
      <div className="emx-modal-overlay" onClick={onClose}>
        {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
        <div className="emx-assign-choice emx-bulk-modal" onClick={e => e.stopPropagation()} style={{ textAlign: 'left' }}>
          <CloseBtn />
          <div className="emx-bulk-heading">
            <h3>Assign Candidates in Bulk</h3>
            <p>Upload a CSV file to assign this exam to multiple candidates</p>
          </div>
          <button className="emx-template-link" onClick={handleDownloadTemplate} style={{ cursor: 'pointer', border: 'none', background: 'none', padding: 0 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6 }}>
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            Download Template
          </button>
          <div
            className={`emx-bulk-upload${bulkDrag ? ' emx-bulk-upload--drag' : ''}`}
            onDragOver={e => { e.preventDefault(); setBulkDrag(true); }}
            onDragLeave={() => setBulkDrag(false)}
            onDrop={e => { e.preventDefault(); setBulkDrag(false); if (e.dataTransfer.files[0]) setBulkFile(e.dataTransfer.files[0]); }}
          >
            <input ref={bulkFileInputRef} type="file" accept=".csv" onChange={e => { if (e.target.files[0]) setBulkFile(e.target.files[0]); }} />
            {bulkFile ? (
              <div className="emx-bulk-file-selected">
                <strong>{bulkFile.name}</strong>
                <button type="button" className="emx-bulk-file-clear" onClick={handleClearBulkFile}>
                  Clear File
                </button>
              </div>
            ) : (
              <>
                <span className="emx-bulk-upload-icon"><UploadIcon /></span>
                <strong>Drag & drop your file here</strong>
                <small>or <span style={{ color: '#2563EB', fontWeight: 600, cursor: 'pointer' }}>browse files</span></small>
              </>
            )}
          </div>
          <div className="emx-bulk-meta emx-bulk-meta-card">
            <span><strong>Accepted format</strong> CSV</span>
            <span><strong>Columns</strong> first_name, last_name, email, mobile_no, college, degree, primary_skill</span>
            <span><strong>Max file size</strong> 5MB</span>
            <span><strong>Limit</strong> 1000 candidates per upload</span>
          </div>
          {assignError && <p style={{ color: '#EF4444', fontSize: '0.78rem', margin: '0 0 0.5rem' }}>{assignError}</p>}
          <div className="emx-bulk-actions">
            <button className="emx-btn emx-btn--primary emx-bulk-submit" onClick={handleBulkUpload} disabled={!bulkFile || bulkUploading}>
              {bulkUploading ? 'Uploading...' : 'Upload & Continue'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="emx-modal-overlay" onClick={onClose}>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <div className="emx-modal emx-assign-modal" onClick={e => e.stopPropagation()}>
        <CloseBtn />
        <div className="emx-modal-header">
          <h2>Assign Candidate</h2>
          <p>Select A Candidate And Configure Exam Details</p>
        </div>
        <div className="emx-modal-body">
          <div
            ref={assignScrollViewportRef}
            className="emx-modal-scroll-viewport"
            style={{ minHeight: '220px' }}
            onScroll={updateAssignScrollThumb}
          >
            <div ref={assignScrollContentRef} className="emx-modal-scroll-content">
          <div ref={searchRef} className="emx-search-wrap" style={{ marginBottom: '0.5rem' }}>
            <label className="emx-field-label">Candidate</label>
            <input
              type="text"
              className="emx-input"
              placeholder="Search by name, email, or ID...."
              value={search}
              onChange={e => { setSearch(e.target.value); setShowDropdown(true); }}
              onFocus={() => setShowDropdown(true)}
            />
            {false && selectedCandidates.length > 0 && (
              <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginTop: '0.4rem' }}>
                {selectedCandidates.map(c => (
                  <div key={c.id} className="emx-chip" style={{ margin: 0 }}>
                    <span title={c.name}>{c.name}</span>
                    <small title={c.email}>{c.email}</small>
                    <button type="button" onClick={() => handleRemoveCandidate(c.id)}>×</button>
                  </div>
                ))}
              </div>
            )}
            {showDropdown && search && (
              <div className="emx-candidate-dropdown" style={{ maxHeight: '180px', overflowY: 'auto' }}>
                {filtered.slice(0, 10).map(c => {
                  const recentAttemptDate = getRecentAttemptDate(c);
                  return (
                    <button key={c.id} type="button" className="emx-candidate-option" onClick={() => handleSelectCandidate(c)}>
                      <span title={c.name}>{c.name}</span>
                      <small title={recentAttemptDate ? `${c.email} - attempted in past 6 months` : c.email}>{recentAttemptDate ? `${c.email} - attempted in past 6 months` : c.email}</small>
                    </button>
                  );
                })}
                {filtered.length === 0 && (
                  <div style={{ padding: '0.65rem 0.9rem', fontSize: '0.78rem', color: '#64748B' }}>No candidates found</div>
                )}
              </div>
            )}
          </div>
          <div className="emx-divider">or add a new candidate</div>
          <div className="emx-candidate-form">
            <div className="emx-form-grid">
              <label>
                <span>First Name<span className="required-star">*</span></span>
                <input type="text" className="emx-input" value={newCandidate.firstName} onChange={e => setNewCandidate(prev => ({ ...prev, firstName: e.target.value }))} required />
              </label>
              <label>
                <span>Last Name<span className="required-star">*</span></span>
                <input type="text" className="emx-input" value={newCandidate.lastName} onChange={e => setNewCandidate(prev => ({ ...prev, lastName: e.target.value }))} required />
              </label>
              <label>
                <span>Email<span className="required-star">*</span></span>
                <input type="email" className="emx-input" value={newCandidate.email} onChange={e => setNewCandidate(prev => ({ ...prev, email: e.target.value }))} required />
              </label>
              <label>
                <span>Mobile No.<span className="required-star">*</span></span>
                <input type="tel" className="emx-input" value={newCandidate.mobileNo} onChange={e => setNewCandidate(prev => ({ ...prev, mobileNo: e.target.value }))} required />
              </label>

              <label>
                <span>Primary Skill<span className="required-star">*</span></span>
                <input
                  type="text"
                  className="emx-input"
                  placeholder="e.g. Frontend, Backend, DevOps..."
                  value={newCandidate.skill}
                  onChange={e => setNewCandidate(prev => ({ ...prev, skill: e.target.value }))}
                  required
                />
              </label>
            </div>
          </div>
          <div className="emx-add-candidate-row">
            <button type="button" className="emx-add-candidate" onClick={handleAddNewCandidate}>Add Candidate</button>
          </div>
          {selectedCandidates.length > 0 && (
            <div className="emx-selected-list">
              {selectedCandidates.map(c => (
                <div key={c.id} className="emx-selected-card">
                  <span className="emx-selected-avatar">{getInitials(c.name)}</span>
                  <div>
                    <strong title={c.name}>{c.name}</strong>
                    {c.skill && <em title={c.skill}>{c.skill}</em>}
                    <small title={c.email}>{c.email}</small>
                  </div>
                  <button type="button" onClick={() => handleRemoveCandidate(c.id)}>×</button>
                </div>
              ))}
              <p>{selectedCandidates.length} candidate{selectedCandidates.length !== 1 ? 's' : ''} selected</p>
            </div>
          )}
            </div>
          </div>
          {assignScrollThumb.visible && (
            <div className="emx-modal-scrollbar" aria-hidden="true">
              <div
                className="emx-modal-scrollbar-thumb"
                style={{
                  height: `${assignScrollThumb.height}px`,
                  transform: `translateY(${assignScrollThumb.top}px)`,
                }}
                onMouseDown={handleAssignScrollDragStart}
              />
            </div>
          )}
        </div>
        <div className="emx-modal-footer" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '0.75rem' }}>
          <label className="emx-schedule-field">
            <span className="emx-schedule-label">
              Exam Start Date & Time <span className="emx-schedule-label-note">(optional)</span>
              <span className="emx-info-wrap">
                <button type="button" className="emx-info-button" aria-label="Exam start help" onClick={e => e.preventDefault()}>i</button>
                <span className="emx-info-tooltip">The exam becomes available only at this scheduled date and time.</span>
              </span>
            </span>
            <span className="emx-schedule-input-wrap">
              <input
                type="datetime-local"
                className="emx-input emx-schedule-input"
                min={minDateTimeLocal}
                value={dueDate}
                onChange={e => setDueDate(e.target.value)}
              />
            </span>
          </label>
          {assignError && <p style={{ color: '#EF4444', fontSize: '0.78rem', margin: 0 }}>{assignError}</p>}
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
            <button className="emx-btn emx-btn--ghost" onClick={onClose}>Cancel</button>
            <button className="emx-btn emx-btn--primary" onClick={handleAssign} disabled={loading || !canAssignCandidates}>
              {loading ? 'Assigning...' : 'Assign Exam'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────
   SIDEBAR ICONS FOR MAIN COMPONENT
───────────────────────────────────────── */
const IconHome = ({ active }) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <g clipPath="url(#clip0_home)">
      <path d="M9.99638 13.9949V8.6635C9.99638 8.48675 9.92617 8.31724 9.80119 8.19226C9.67621 8.06728 9.5067 7.99707 9.32995 7.99707H6.66423C6.48748 7.99707 6.31798 8.06728 6.193 8.19226C6.06802 8.31724 5.9978 8.48675 5.9978 8.6635V13.9949" stroke={active ? 'white' : '#26282B'} strokeWidth="1.33286" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M1.99927 6.6641C1.99922 6.47021 2.04148 6.27865 2.12308 6.10278C2.20469 5.9269 2.32368 5.77095 2.47177 5.6458L7.13678 1.64788C7.37735 1.44456 7.68215 1.33301 7.99714 1.33301C8.31212 1.33301 8.61693 1.44456 8.8575 1.64788L13.5225 5.6458C13.6706 5.77095 13.7896 5.9269 13.8712 6.10278C13.9528 6.27865 13.9951 6.47021 13.995 6.6641V12.662C13.995 13.0155 13.8546 13.3545 13.6046 13.6044C13.3547 13.8544 13.0156 13.9948 12.6621 13.9948H3.33213C2.97863 13.9948 2.63961 13.8544 2.38965 13.6044C2.13969 13.3545 1.99927 13.0155 1.99927 12.662V6.6641Z" stroke={active ? 'white' : '#26282B'} strokeWidth="1.33286" strokeLinecap="round" strokeLinejoin="round" />
    </g>
    <defs><clipPath id="clip0_home"><rect width="15.9943" height="15.9943" fill="white" /></clipPath></defs>
  </svg>
);

const IconCandidates = ({ active }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke={active ? 'white' : '#26282B'} strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="9" cy="7" r="4" stroke={active ? 'white' : '#26282B'} strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" stroke={active ? 'white' : '#26282B'} strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" stroke={active ? 'white' : '#26282B'} strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const IconSections = ({ active }) => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M2.5 5H17.5" stroke={active ? 'white' : '#26282B'} strokeWidth="1.66667" strokeLinecap="round" />
    <path d="M2.5 10H17.5" stroke={active ? 'white' : '#26282B'} strokeWidth="1.66667" strokeLinecap="round" />
    <path d="M2.5 15H17.5" stroke={active ? 'white' : '#26282B'} strokeWidth="1.66667" strokeLinecap="round" />
  </svg>
);

const IconQuestionBank = ({ active }) => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M10 14.167H10.0083" stroke={active ? 'white' : '#26282B'} strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M12.4999 1.66699H4.99992C4.55789 1.66699 4.13397 1.84259 3.82141 2.15515C3.50885 2.46771 3.33325 2.89163 3.33325 3.33366V16.667C3.33325 17.109 3.50885 17.5329 3.82141 17.8455C4.13397 18.1581 4.55789 18.3337 4.99992 18.3337H14.9999C15.4419 18.3337 15.8659 18.1581 16.1784 17.8455C16.491 17.5323 16.6666 17.109 16.6666 16.667V5.83366L12.4999 1.66699Z" stroke={active ? 'white' : '#26282B'} strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M7.58325 7.49982C7.78323 6.94867 8.17056 6.48535 8.67753 6.19084C9.18449 5.89633 9.77884 5.78937 10.3567 5.88865C10.9345 5.98793 11.4591 6.28715 11.8387 6.73399C12.2182 7.18083 12.4287 7.74686 12.4333 8.33315C12.4333 9.99982 9.93325 10.8331 9.93325 10.8331" stroke={active ? 'white' : '#26282B'} strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const IconExamManagement = ({ active }) => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M12.5001 1.66699H7.50008C7.03984 1.66699 6.66675 2.04009 6.66675 2.50033V4.16699C6.66675 4.62723 7.03984 5.00033 7.50008 5.00033H12.5001C12.9603 5.00033 13.3334 4.62723 13.3334 4.16699V2.50033C13.3334 2.04009 12.9603 1.66699 12.5001 1.66699Z" stroke={active ? 'white' : '#26282B'} strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M13.3333 3.33301H14.9999C15.4419 3.33301 15.8659 3.5086 16.1784 3.82116C16.491 4.13372 16.6666 4.55765 16.6666 4.99967V16.6663C16.6666 17.1084 16.491 17.5323 16.1784 17.8449C15.8659 18.1574 15.4419 18.333 14.9999 18.333H4.99992C4.55789 18.333 4.13397 18.1574 3.82141 17.8449C3.50885 17.5323 3.33325 17.1084 3.33325 16.6663V4.99967C3.33325 4.55765 3.50885 4.13372 3.82141 3.82116C4.13397 3.5086 4.55789 3.33301 4.99992 3.33301H6.66659" stroke={active ? 'white' : '#26282B'} strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M10 9.16699H13.3333" stroke={active ? 'white' : '#26282B'} strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M10 13.333H13.3333" stroke={active ? 'white' : '#26282B'} strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M6.66675 9.16699H6.67508" stroke={active ? 'white' : '#26282B'} strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M6.66675 13.333H6.67508" stroke={active ? 'white' : '#26282B'} strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const IconAnalytics = ({ active }) => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path d="M2.5 2.5V15.8333C2.5 16.2754 2.67559 16.6993 2.98816 17.0118C3.30072 17.3244 3.72464 17.5 4.16667 17.5H17.5" stroke={active ? 'white' : '#26282B'} strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M15 14.1667V7.5" stroke={active ? 'white' : '#26282B'} strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M10.8333 14.167V4.16699" stroke={active ? 'white' : '#26282B'} strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M6.66675 14.167V11.667" stroke={active ? 'white' : '#26282B'} strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const IconDrive = ({ active }) => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M3.333 6.667h13.334M5 3.333h10c.92 0 1.667.746 1.667 1.667v10c0 .92-.746 1.667-1.667 1.667H5A1.667 1.667 0 0 1 3.333 15V5c0-.92.746-1.667 1.667-1.667Z" stroke={active ? 'white' : '#26282B'} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/><path d="M7 10h.01M10 10h3M7 13h.01M10 13h3" stroke={active ? 'white' : '#26282B'} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
);

/* ─────────────────────────────────────────
   SIDEBAR ITEMS CONFIG
───────────────────────────────────────── */
const sidebarItems = [
  { label: 'Home', Icon: IconHome, path: '/recruiter' },
  { label: 'Drives', Icon: IconDrive, path: '/drives' },
  { label: 'Exam Management', Icon: IconExamManagement, path: '/exam-management' },
  { label: 'Candidates', Icon: IconCandidates, path: '/candidates' },
  { label: 'Sections', Icon: IconSections, path: '/sections' },
  { label: 'Question Bank', Icon: IconQuestionBank, path: '/question-bank' },
  { label: 'Analytics', Icon: IconAnalytics, path: '/analytics' },
];

/* ─────────────────────────────────────────
   MAIN EXAM MANAGEMENT PAGE COMPONENT
───────────────────────────────────────── */
const ExamManagement = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All Categories');
  const [showCatDropdown, setShowCatDropdown] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [exams, setExams] = useState([]);
  const [loadingExams, setLoadingExams] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [examToEdit, setExamToEdit] = useState(null);
  const [examToDuplicate, setExamToDuplicate] = useState(null);
  const [examToDelete, setExamToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [examToPublish, setExamToPublish] = useState(null);
  const [examToAssign, setExamToAssign] = useState(null);
  const [examDetail, setExamDetail] = useState(null);
  const [examDetailCandidates, setExamDetailCandidates] = useState([]);
  const [examDetailLoading, setExamDetailLoading] = useState(false);
  const [examDetailSearch, setExamDetailSearch] = useState('');
  const [examDetailStatusFilter, setExamDetailStatusFilter] = useState('all');
  const [candidateToUnassign, setCandidateToUnassign] = useState(null);
  const [candidateDetail, setCandidateDetail] = useState(null);
  const [candidateEvidenceLoading, setCandidateEvidenceLoading] = useState(false);
  const [candidateAnswersLoading, setCandidateAnswersLoading] = useState(false);
  const [candidateAnswerPage, setCandidateAnswerPage] = useState(1);
  const [toast, setToast] = useState(null);
  const [examDrives, setExamDrives] = useState([]);

  const storedUser = getStoredUserForRole('recruiter');

  const fetchExams = useCallback(() => {
    setLoadingExams(true);
    setFetchError('');
    API.get('/exam/recruiter')
      .then(res => setExams(Array.isArray(res.data) ? res.data : []))
      .catch(() => setFetchError('Failed to load exams. Please refresh.'))
      .finally(() => setLoadingExams(false));
  }, []);

  useEffect(() => {
    fetchExams();
  }, [fetchExams]);

  useEffect(() => {
    API.get('/drives')
      .then(res => setExamDrives(Array.isArray(res.data?.drives) ? res.data.drives : []))
      .catch(() => setExamDrives([]));
  }, []);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (showCatDropdown && !e.target.closest('.em-categories-dropdown')) {
        setShowCatDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [showCatDropdown]);

  const handleExamCreated = ({ mode } = {}) => {
    setExamToEdit(null);
    setExamToDuplicate(null);
    setToast({
      message: mode === 'edit' ? 'Exam updated successfully.' : 'Exam created successfully.',
      type: 'success',
    });
    fetchExams();
  };

  const handleCloseCreateModal = (closeMeta = {}) => {
    const wasCreatingExam = !examToEdit;

    setShowCreateModal(false);
    setExamToEdit(null);
    setExamToDuplicate(null);

    if (wasCreatingExam && closeMeta.reason === 'overlay' && closeMeta.draftSaved) {
      setToast({
        message: 'Draft saved. You can continue creating the exam later.',
        type: 'success',
      });
    }
  };

  const handleEditExam = (exam) => {
    setExamToEdit(exam);
    setExamToDuplicate(null);
    setShowCreateModal(true);
  };

  const getDuplicateTitle = (title = '') => {
    const trimmed = String(title || '').trim();
    if (!trimmed) return 'Untitled Exam2';

    const normalizedTitles = new Set(exams.map((item) => String(item.title || '').trim().toLowerCase()));
    let suffix = 2;
    let nextTitle = `${trimmed}${suffix}`;
    while (normalizedTitles.has(nextTitle.toLowerCase())) {
      suffix += 1;
      nextTitle = `${trimmed}${suffix}`;
    }
    return nextTitle;
  };

  const handleDuplicateExam = (exam) => {
    setExamToEdit(null);
    setExamToDuplicate({
      ...exam,
      title: getDuplicateTitle(exam.title),
      status: 'inactive',
      draftKey: `exam-management:duplicate:${exam.id}`,
    });
    setShowCreateModal(true);
  };

  const handleDeleteExam = (examId) => {
    setDeleteError('');
    setExamToDelete(examId);
  };

  const handleDeleteConfirm = async () => {
    if (!examToDelete) return;
    setIsDeleting(true);
    setDeleteError('');

    try {
      await API.delete(`/exam/${examToDelete}`);
      setExams(prev => prev.filter(e => e.id !== examToDelete));
      setExamToDelete(null);
    } catch (err) {
      setDeleteError(err.response?.data?.message || 'Failed to update exam. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  // DEV MODE: no-op, kept for compatibility
  const handlePublishExam = async (examId) => {
    console.log('[DEV] Publish exam:', examId);
  };

  const handleOpenExamDetail = (exam) => {
    const examStatus = (exam?.status || 'inactive').toLowerCase();
    if (examStatus === 'inactive' || examStatus === 'draft') {
      return;
    }

    setExamDetail(exam);
    setExamDetailCandidates([]);
    setExamDetailSearch('');
    setExamDetailStatusFilter('all');
    setExamDetailLoading(true);
    API.get(`/exam/${exam.id}/candidates`)
      .then(res => setExamDetailCandidates(Array.isArray(res.data?.candidates) ? res.data.candidates : []))
      .catch(() => setExamDetailCandidates([]))
      .finally(() => setExamDetailLoading(false));
  };

  const formatViolationLabel = (submitReason, tabSwitchCount) => {
    if (submitReason === 'tab_switch') return `Tab switch (${tabSwitchCount || 0})`;
    if (submitReason === 'screen_share_stopped') return 'Screen share stopped';
    if (submitReason === 'timeout') return 'Timed out';
    if (submitReason === 'manual') return 'None';
    return submitReason ? submitReason.replace(/_/g, ' ') : 'None';
  };

  const handleOpenCandidateDetail = (candidate) => {
    setCandidateDetail({
      ...candidate,
      examTitle: examDetail?.title || 'Assessment',
      screenshots: [],
      answers: [],
    });
    setCandidateAnswerPage(1);
    setCandidateEvidenceLoading(true);
    setCandidateAnswersLoading(true);

    if (candidate.attempt_id && examDetail?.id) {
      API.get(`/exam/screenshots/${candidate.id}`, { params: { examId: examDetail.id } })
        .then((res) => {
          const screenshots = Array.isArray(res.data?.screenshots)
            ? res.data.screenshots.map((entry) => (typeof entry === 'string' ? entry : entry?.filename)).filter(Boolean)
            : [];
          setCandidateDetail((prev) => (prev ? { ...prev, screenshots } : prev));
        })
        .catch(() => {
          setCandidateDetail((prev) => (prev ? { ...prev, screenshots: [] } : prev));
        })
        .finally(() => setCandidateEvidenceLoading(false));
    } else {
      setCandidateDetail((prev) => (prev ? { ...prev, screenshots: [] } : prev));
      setCandidateEvidenceLoading(false);
    }

    if (examDetail?.id) {
      API.get(`/exam/${examDetail.id}/candidates/${candidate.id}/answers`)
        .then((res) => {
          const answers = Array.isArray(res.data?.answers) ? res.data.answers : [];
          setCandidateDetail((prev) => (prev ? { ...prev, answers } : prev));
        })
        .catch(() => {
          setCandidateDetail((prev) => (prev ? { ...prev, answers: [] } : prev));
        })
        .finally(() => setCandidateAnswersLoading(false));
    } else {
      setCandidateAnswersLoading(false);
    }
  };

  const handleExportCandidateCsv = () => {
    if (!candidateDetail) return;

    const escapeCsv = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const summaryRows = [
      ['Field', 'Value'],
      ['Name', candidateDetail.name || ''],
      ['Email', candidateDetail.email || ''],
      ['College', candidateDetail.college_name || candidateDetail.college || ''],
      ['Degree', candidateDetail.degree || ''],
      ['Exam', candidateDetail.examTitle || ''],
      ['Status', candidateDetail.submit_reason === 'tab_switch' ? 'Defaulter' : (candidateDetail.assignment_status || '')],
      ['Score', candidateDetail.percentage != null ? `${candidateDetail.percentage}%` : ''],
      ['Submitted On', candidateDetail.submitted_at ? new Date(candidateDetail.submitted_at).toLocaleString('en-IN') : ''],
      ['Violation', formatViolationLabel(candidateDetail.submit_reason, candidateDetail.tab_switch_count)],
    ];

    const answerRows = [
      [],
      ['Question', 'Selected Answer', 'Correct Answer', 'Result'],
      ...(candidateDetail.answers || []).map((answer) => [
        answer.question_text || '',
        answer.selected_option || 'Not answered',
        answer.correct_option || '',
        answer.is_correct === true ? 'Correct' : answer.is_correct === false ? 'Incorrect' : 'Not answered',
      ]),
    ];

    const csv = [...summaryRows, ...answerRows]
      .map(row => row.map(escapeCsv).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${(candidateDetail.name || 'candidate').replace(/[^a-z0-9_-]+/gi, '_')}_details.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleOpenScreenshot = async (filename) => {
    try {
      const response = await API.get(`/exam/screenshot/${encodeURIComponent(filename)}`, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(response.data);
      window.open(blobUrl, '_blank', 'noopener,noreferrer');
      window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60 * 1000);
    } catch (err) {
      setToast({
        message: err.response?.data?.message || 'Unable to open screenshot.',
        type: 'error',
      });
    }
  };

  const handleUpdateStatusDirect = async (examId, status) => {
    try {
      await API.put(`/exam/${examId}/status`, { status });
      setExams(prev => prev.map(e => e.id === examId ? { ...e, status } : e));
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update exam status');
    }
  };

  const normalizedExamDetailSearch = examDetailSearch.trim().toLowerCase();
  const filteredExamDetailCandidates = examDetailCandidates
    .filter(candidate => {
      if (!normalizedExamDetailSearch) return true;
      return `${candidate.name || ''} ${candidate.email || ''}`.toLowerCase().includes(normalizedExamDetailSearch);
    })
    .filter(candidate => {
      if (examDetailStatusFilter === 'all') return true;
      const status = (candidate.assignment_status || 'assigned').toLowerCase();
      if (examDetailStatusFilter === 'completed') return ['completed', 'results_published'].includes(status);
      if (examDetailStatusFilter === 'ongoing') return status === 'ongoing';
      if (examDetailStatusFilter === 'assigned') return ['scheduled', 'assigned', 'active'].includes(status);
      return status === examDetailStatusFilter;
    })
    .sort((a, b) => {
      if (!normalizedExamDetailSearch) {
        return new Date(b.assigned_at || 0).getTime() - new Date(a.assigned_at || 0).getTime();
      }
      const aName = String(a.name || '').toLowerCase();
      const bName = String(b.name || '').toLowerCase();
      const aEmail = String(a.email || '').toLowerCase();
      const bEmail = String(b.email || '').toLowerCase();
      const aStarts = aName.startsWith(normalizedExamDetailSearch) || aEmail.startsWith(normalizedExamDetailSearch);
      const bStarts = bName.startsWith(normalizedExamDetailSearch) || bEmail.startsWith(normalizedExamDetailSearch);
      if (aStarts !== bStarts) return aStarts ? -1 : 1;
      return aName.localeCompare(bName);
    });
  const candidateAnswers = candidateDetail?.answers || [];
  const answerPageSize = Math.max(candidateAnswers.length, 1);
  const answerTotalPages = Math.max(1, Math.ceil(candidateAnswers.length / answerPageSize));
  const visibleCandidateAnswers = candidateAnswers;
  const candidateSectionScores = Object.values(candidateAnswers.reduce((acc, answer) => {
    const sectionName = answer.section_name || 'General';
    const marks = Number(answer.marks || 1);
    if (!acc[sectionName]) {
      acc[sectionName] = { name: sectionName, score: 0, total: 0 };
    }
    acc[sectionName].total += marks;
    if (answer.is_correct === true) {
      acc[sectionName].score += marks;
    }
    return acc;
  }, {}));
  const examDetailCounts = examDetailCandidates.reduce((acc, candidate) => {
    const status = (candidate.assignment_status || 'assigned').toLowerCase();
    if (['completed', 'results_published'].includes(status)) acc.completed += 1;
    else if (status === 'ongoing') acc.ongoing += 1;
    else acc.assigned += 1;
    return acc;
  }, { assigned: 0, ongoing: 0, completed: 0 });

  const handleUnassignCandidate = async () => {
    if (!examDetail?.id || !candidateToUnassign?.id) return;
    const candidateKey = candidateToUnassign.user_id || candidateToUnassign.id;
    try {
      await API.delete(`/exam/${examDetail.id}/candidates/${candidateKey}`);
      setExamDetailCandidates(prev => prev.filter(candidate => (candidate.user_id || candidate.id) !== candidateKey));
      setCandidateToUnassign(null);
      setToast({ message: 'Candidate unassigned from this exam.', type: 'success' });
    } catch (err) {
      setToast({ message: err.response?.data?.message || 'Failed to unassign candidate.', type: 'error' });
    }
  };

  return (
    <div className="em-page">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* ── Navbar ── */}
      <nav className="em-navbar">
        <img src={logo} alt="logo" className="em-logo" />
        <div className="em-navbar-title">
          <h1 className="em-navbar-heading">Exam Management</h1>
          <p className="em-navbar-sub">{exams.length} exams available</p>
        </div>
        <div className="em-navbar-right">
          <button className="em-create-btn" onClick={() => { setExamToEdit(null); setShowCreateModal(true); }}>
            <span className="em-create-plus">+</span> Create Exam
          </button>
          <div className="em-navbar-profile">
            <ProfileDropdown user={storedUser.name || storedUser.email} email={storedUser.email} />
          </div>
        </div>
      </nav>

      {/* ── Sidebar ── */}
      <aside className="em-sidebar">
        {sidebarItems
          .filter(item => !(storedUser.role === 'junior_recruiter' && item.path === '/question-bank'))
          .map(({ label, Icon, path }, index) => {
          const isActive = location.pathname === path;
          return (
            <div
              key={label}
              onClick={() => { 
                if (path) {
                  navigate(path);
                }
              }}
              className={`em-sidebar-item ${isActive ? 'active' : ''}`}
              style={{ top: `${140 + index * 64}px` }}
            >
              <span className="em-sidebar-icon"><Icon active={isActive} /></span>
              <span className="em-sidebar-text">{label}</span>
            </div>
          );
        })}
      </aside>

      {/* ── Main Content ── */}
      <main className="em-main">
        <div className="em-main-inner">

          {/* Filter Bar */}
          <div className="em-filter-wrapper">
            <div className="em-filter-container">
              <div className="em-search-input-box">
                <div className="em-search-icon">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <circle cx="8.5" cy="8.5" r="5.5" stroke="#9CA3AF" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M15 15L12 12" stroke="#9CA3AF" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <input
                  className="em-search-text"
                  type="text"
                  placeholder="Search exams....."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                />
              </div>
              <div className="em-filter-right-group">
                <div className="em-filter-label">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                  </svg>
                  Filters
                </div>
                <div className="em-categories-dropdown" onClick={() => setShowCatDropdown(!showCatDropdown)}>
                  <div className="em-cat-selected">
                    <div className="em-cat-icon">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path fillRule="evenodd" clipRule="evenodd" d="M8.25 1.91699C8.44891 1.91699 8.63968 1.99601 8.78033 2.13666C8.92098 2.27731 9 2.46808 9 2.66699V13.333C9 13.5319 8.92098 13.7227 8.78033 13.8633C8.63968 14.004 8.44891 14.083 8.25 14.083C8.05109 14.083 7.86032 14.004 7.71967 13.8633C7.57902 13.7227 7.5 13.5319 7.5 13.333V2.66699C7.5 2.46808 7.57902 2.27731 7.71967 2.13666C7.86032 1.99601 8.05109 1.91699 8.25 1.91699ZM12.25 5.91699C12.4489 5.91699 12.6397 5.99601 12.7803 6.13666C12.921 6.27731 13 6.46808 13 6.66699V13.333C13 13.5319 12.921 13.7227 12.7803 13.8633C12.6397 14.004 12.4489 14.083 12.25 14.083C12.0511 14.083 11.8603 14.004 11.7197 13.8633C11.579 13.7227 11.5 13.5319 11.5 13.333V6.66699C11.5 6.46808 11.579 6.27731 11.7197 6.13666C11.8603 5.99601 12.0511 5.91699 12.25 5.91699ZM5 9.33299C5 9.13408 4.92098 8.94331 4.78033 8.80266C4.63968 8.66201 4.44891 8.58299 4.25 8.58299C4.05109 8.58299 3.86032 8.66201 3.71967 8.80266C3.57902 8.94331 3.5 9.13408 3.5 9.33299V13.333C3.5 13.5319 3.57902 13.7227 3.71967 13.8633C3.86032 14.004 4.05109 14.083 4.25 14.083C4.44891 14.083 4.63968 14.004 4.78033 13.8633C4.92098 13.7227 5 13.5319 5 13.333V9.33299Z" fill="#5B92E9" />
                      </svg>
                    </div>
                    <span className="em-cat-text">{categoryFilter}</span>
                    <div className="em-cat-arrow">
                      <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
                        <path d="M1 1.5L6 6.5L11 1.5" stroke="#181D1F" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  </div>
                  {showCatDropdown && (
                    <div className="em-cat-dropdown-menu">
                      {['All Categories', 'Active', 'Draft', 'Ongoing', 'Completed'].map(cat => (
                        <div
                          key={cat}
                          className={`em-cat-dropdown-option ${categoryFilter === cat ? 'em-cat-dropdown-option--selected' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setCategoryFilter(cat);
                            setShowCatDropdown(false);
                          }}
                        >
                          {cat}
                          {categoryFilter === cat && (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Cards grid */}
          <div className="em-cards-grid">
            {loadingExams && (
              <p style={{ color: '#6B7280', padding: '24px' }}>Loading exams…</p>
            )}
            {fetchError && (
              <p style={{ color: '#EF4444', padding: '24px' }}>{fetchError}</p>
            )}
            {!loadingExams && !fetchError && exams.length === 0 && (
              <p style={{ color: '#6B7280', padding: '24px' }}>No exams yet. Click "+ Create Exam" to get started.</p>
            )}
            {exams
              .filter(e => {
                const matchesSearch = (e.title || '').toLowerCase().includes(searchQuery.toLowerCase());
                const normalizedStatus = (e.status || '').toLowerCase();
                if (normalizedStatus === 'archived') return false;
                if (categoryFilter === 'All Categories') return matchesSearch;
                const statusMap = {
                  'Active': ['active'],
                  'Draft': ['inactive', 'draft'],
                  'Ongoing': ['ongoing'],
                  'Completed': ['completed', 'results_published'],
                };
                const targetStatuses = statusMap[categoryFilter] || [];
                return matchesSearch && targetStatuses.includes(normalizedStatus);
              })
              .map(exam => (
                <ExamCard
                  key={exam.id}
                  exam={exam}
                  onEdit={handleEditExam}
                  onDuplicate={handleDuplicateExam}
                  onDelete={handleDeleteExam}
                  onPublish={handlePublishExam}
                  onUpdateStatus={handleUpdateStatusDirect}
                  onAssign={(exam) => setExamToAssign(exam)}
                  onPublishResult={(exam) => setExamToPublish(exam)}
                  onViewDetail={handleOpenExamDetail}
                />
              ))
            }
          </div>

        </div>
      </main>

      {showCreateModal && (
        <CreateExamModal
          onClose={handleCloseCreateModal}
          onSuccess={handleExamCreated}
          examToEdit={examToEdit}
          initialExamData={examToDuplicate}
          modalTitle={examToEdit ? 'Edit Exam' : examToDuplicate ? 'Duplicate Exam' : 'Create Exam'}
        />
      )}

      {/* ── Delete Confirmation Modal ── */}
      {examToDelete && (
          <div className="em-confirm-overlay" onClick={() => !isDeleting && setExamToDelete(null)}>
            <div className="em-confirm-dialog" onClick={e => e.stopPropagation()}>
              <button className="em-confirm-close" onClick={() => setExamToDelete(null)}>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M13 1L1 13M1 1L13 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              <div className="em-confirm-icon">
                <svg width="33" height="33" viewBox="0 0 33 33" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M27.8238 6.18259H5.15258C4.87927 6.18259 4.61716 6.29116 4.4239 6.48442C4.23064 6.67767 4.12207 6.93979 4.12207 7.2131C4.12207 7.4864 4.23064 7.74852 4.4239 7.94178C4.61716 8.13504 4.87927 8.24361 5.15258 8.24361H6.18309V26.7928C6.18309 27.3394 6.40023 27.8636 6.78675 28.2501C7.17326 28.6367 7.69749 28.8538 8.24411 28.8538H24.7323C25.2789 28.8538 25.8031 28.6367 26.1896 28.2501C26.5761 27.8636 26.7933 27.3394 26.7933 26.7928V8.24361H27.8238C28.0971 8.24361 28.3592 8.13504 28.5525 7.94178C28.7457 7.74852 28.8543 7.4864 28.8543 7.2131C28.8543 6.93979 28.7457 6.67767 28.5525 6.48442C28.3592 6.29116 28.0971 6.18259 27.8238 6.18259ZM24.7323 26.7928H8.24411V8.24361H24.7323V26.7928ZM10.3051 3.09106C10.3051 2.81775 10.4137 2.55563 10.607 2.36238C10.8002 2.16912 11.0623 2.06055 11.3356 2.06055H21.6407C21.914 2.06055 22.1762 2.16912 22.3694 2.36238C22.5627 2.55563 22.6712 2.81775 22.6712 3.09106C22.6712 3.36437 22.5627 3.62648 22.3694 3.81974C22.1762 4.013 21.914 4.12157 21.6407 4.12157H11.3356C11.0623 4.12157 10.8002 4.013 10.607 3.81974C10.4137 3.62648 10.3051 3.36437 10.3051 3.09106Z" fill="#DC2626" />
                </svg>
              </div>
              <div className="em-confirm-copy">
                <h2>Delete this exam permanently?</h2>
                <p>
                  This exam will be deleted permanently.<br/>This action cannot be undone.
                </p>
                {deleteError && <div className="em-confirm-error">{deleteError}</div>}
              </div>
              <div className="em-confirm-actions">
                <button
                  className="em-confirm-btn em-confirm-cancel"
                  onClick={() => setExamToDelete(null)}
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button
                  className="em-confirm-btn em-confirm-delete"
                  onClick={handleDeleteConfirm}
                  disabled={isDeleting}
                >
                  {isDeleting ? 'Deleting...' : 'Yes, Delete'}
                </button>
              </div>
            </div>
          </div>
      )}

      {/* ── Publish Result Modal ── */}
      {examToPublish && (
        <PublishResultModal
          exam={examToPublish}
          onClose={(shouldRefresh) => {
            setExamToPublish(null);
            if (shouldRefresh === true) fetchExams();
          }}
        />
      )}

      {/* ── Assign Exam Modal ── */}
      {examToAssign && (
        <AssignExamModal
          exam={examToAssign}
          onClose={() => {
            setExamToAssign(null);
            fetchExams();
          }}
        />
      )}

      {/* ── Exam Detail Modal ── */}
      {examDetail && (() => {
        const detailSections = parseSectionsConfig(examDetail.sections_config).map(normalizeExamSection);
        const compulsorySections = detailSections.filter(section => section.isCompulsory || !section.isOptional);
        const optionalSections = detailSections.filter(section => !section.isCompulsory && section.isOptional);
        const linkedDriveNames = examDrives
          .filter(drive => String(drive.exam_id || '') === String(examDetail.id || ''))
          .map(drive => drive.name)
          .filter(Boolean)
          .join(', ');
        return (
        <div className="emx-modal-overlay" onClick={() => setExamDetail(null)}>
          <div onClick={e => e.stopPropagation()} style={{
            background: '#fff', borderRadius: '16px', width: 'min(1500px, 94vw)',
            maxHeight: '96vh', display: 'flex', flexDirection: 'column',
            boxShadow: '0 24px 64px rgba(15,23,42,0.22)', overflow: 'hidden',
          }}>
            {/* Header */}
            <div style={{ padding: '22px 36px 14px', borderBottom: '1px solid #F1F5F9', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#111827' }}>{examDetail.title}</h2>
                <p style={{ margin: '5px 0 0', fontSize: '13px', color: '#64748B' }}>{examDetail.description || 'No description'}</p>
              </div>
              <button onClick={() => setExamDetail(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', fontSize: '22px', lineHeight: 1, padding: '2px 6px' }}>×</button>
            </div>

            {/* Exam stats row */}
            <div style={{ display: 'flex', gap: '14px', padding: '14px 36px', borderBottom: '1px solid #F1F5F9' }}>
              {[
                { label: 'Status', value: (examDetail.status || 'draft').toUpperCase() },
                { label: 'Sections', value: examDetail.section_count || '—' },
                { label: 'Created', value: examDetail.created_at ? new Date(examDetail.created_at).toLocaleDateString('en-IN') : '—' },
                { label: 'Drive', value: linkedDriveNames || '—' },
              ].map(({ label, value }) => (
                <div key={label} style={{ flex: '1 1 0', minWidth: 0, background: '#F8FAFC', borderRadius: '10px', padding: '12px 16px' }}>
                  <div style={{ fontSize: '11px', color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>{label}</div>
                  <div title={String(value)} style={{ fontSize: '15px', fontWeight: 700, color: '#1E293B', marginTop: '5px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value}</div>
                </div>
              ))}
            </div>

            {detailSections.length > 0 && (
              <div style={{ order: 2, padding: '10px 36px 14px', borderTop: '1px solid #F1F5F9' }}>
                <h3 style={{ margin: '0 0 12px', fontSize: '15px', fontWeight: 700, color: '#374151' }}>Section Details</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  {[
                    { title: 'Compulsory Sections', items: compulsorySections, badgeColor: '#2563EB', bg: '#EFF6FF' },
                    { title: 'Optional Sections', items: optionalSections, badgeColor: '#F59E0B', bg: '#FFFBEB' },
                  ].map(group => (
                    <div key={group.title} style={{ minWidth: 0, background: '#F8FAFC', border: '1px solid #E5E7EB', borderRadius: '12px', padding: '14px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', marginBottom: '10px' }}>
                        <span style={{ fontSize: '13px', fontWeight: 700, color: '#1F2937' }}>{group.title}</span>
                        <span style={{ background: group.bg, color: group.badgeColor, borderRadius: '999px', padding: '2px 9px', fontSize: '11px', fontWeight: 700 }}>{group.items.length}</span>
                      </div>
                      {group.items.length === 0 ? (
                        <p style={{ margin: 0, color: '#94A3B8', fontSize: '12px' }}>None</p>
                      ) : (
                        <div style={{ display: 'grid', gap: '8px', maxHeight: '72px', overflowY: 'auto', paddingRight: '4px' }}>
                          {group.items.map(section => (
                            <div key={`${group.title}-${section.id}`} style={{ display: 'grid', gridTemplateColumns: 'minmax(120px, 1fr) auto', gap: '10px', alignItems: 'center', background: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '9px 10px' }}>
                              <div style={{ minWidth: 0 }}>
                                <div style={{ color: '#111827', fontSize: '13px', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{section.name}</div>
                                <div style={{ color: '#64748B', fontSize: '11px', marginTop: '3px' }}>
                                  Easy {section.easy} · Medium {section.medium} · Hard {section.difficult}
                                </div>
                              </div>
                              <div style={{ color: '#475569', fontSize: '12px', fontWeight: 600, textAlign: 'right', whiteSpace: 'nowrap' }}>
                                {section.questions} Qs · {section.minutes} Min
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Candidates list */}
            <div style={{ order: 1, flex: '1 1 380px', display: 'flex', flexDirection: 'column', minHeight: 0, padding: '0 36px 14px', overflowY: 'auto' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(260px, 1fr) minmax(240px, 360px) 132px', alignItems: 'center', gap: '12px', padding: '14px 0 10px', position: 'sticky', top: 0, background: '#fff', zIndex: 2 }}>
                <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 700, color: '#374151', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  Assigned Candidates
                  <span style={{ marginLeft: '8px', background: '#EFF6FF', color: '#2563EB', borderRadius: '20px', padding: '2px 10px', fontSize: '12px', fontWeight: 600 }}>
                    {examDetailLoading ? '...' : filteredExamDetailCandidates.length}
                  </span>
                  {[
                    { key: 'completed', label: 'Completed', count: examDetailCounts.completed, color: '#059669' },
                    { key: 'ongoing', label: 'Ongoing', count: examDetailCounts.ongoing, color: '#2563EB' },
                    { key: 'assigned', label: 'Assigned', count: examDetailCounts.assigned, color: '#F59E0B' },
                  ].map(pill => (
                    <button
                      key={pill.key}
                      type="button"
                      onClick={() => setExamDetailStatusFilter(prev => prev === pill.key ? 'all' : pill.key)}
                      style={{
                        border: `1px solid ${examDetailStatusFilter === pill.key ? pill.color : '#D8E0EA'}`,
                        background: examDetailStatusFilter === pill.key ? `${pill.color}14` : '#FFFFFF',
                        color: pill.color,
                        borderRadius: '999px',
                        padding: '3px 10px',
                        fontSize: '11px',
                        fontWeight: 800,
                        cursor: 'pointer',
                      }}
                    >
                      {pill.label} {pill.count}
                    </button>
                  ))}
                </h3>
                <input
                  type="text"
                  value={examDetailSearch}
                  onChange={(e) => setExamDetailSearch(e.target.value)}
                  placeholder="Search by name or email..."
                  style={{ width: '100%', height: '36px', border: '1px solid #D8E0EA', borderRadius: '8px', padding: '0 12px', fontSize: '13px', color: '#475569', outline: 'none', boxSizing: 'border-box' }}
                />
                {(() => {
                  const isCompleted = ['completed', 'results_published'].includes((examDetail.status || '').toLowerCase());
                  return (
                    <button
                      onClick={() => { if (!isCompleted) { setExamDetail(null); setExamToAssign(examDetail); } }}
                      disabled={isCompleted}
                      style={{ width: '132px', background: isCompleted ? '#94A3B8' : '#2563EB', color: '#fff', border: 'none', borderRadius: '8px', padding: '8px 12px', fontSize: '12px', fontWeight: 700, cursor: isCompleted ? 'not-allowed' : 'pointer', opacity: isCompleted ? 0.6 : 1 }}
                    >
                      + Assign More
                    </button>
                  );
                })()}
              </div>

              {examDetailLoading ? (
                <p style={{ color: '#94A3B8', fontSize: '13px' }}>Loading candidates…</p>
              ) : filteredExamDetailCandidates.length === 0 ? (
                <p style={{ color: '#94A3B8', fontSize: '13px', textAlign: 'center', padding: '40px 0' }}>No candidates assigned yet.</p>
              ) : (
                <div style={{ border: '1px solid #E5E7EB', borderRadius: '12px', overflowY: 'auto', overflowX: 'hidden', maxHeight: '270px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', tableLayout: 'fixed' }}>
                    <colgroup>
                      <col style={{ width: '17%' }} />
                      <col style={{ width: '21%' }} />
                      <col style={{ width: '13%' }} />
                      <col style={{ width: '12%' }} />
                      <col style={{ width: '10%' }} />
                      <col style={{ width: '12%' }} />
                      <col style={{ width: '8%' }} />
                      <col style={{ width: '7%' }} />
                    </colgroup>
                    <thead>
                      <tr style={{ background: '#F8FAFC' }}>
                        {['Candidate', 'Email', 'College', 'Degree', 'Skill', 'Status', 'Score', 'Actions'].map(h => (
                          <th key={h} style={{ padding: '11px 16px', textAlign: 'left', fontWeight: 600, color: '#64748B', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #E5E7EB' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredExamDetailCandidates.map((c, i) => {
                        const status = (c.assignment_status || 'assigned').toLowerCase();
                        const isDefaulter = c.submit_reason === 'tab_switch';
                        const statusLabel = isDefaulter ? 'Defaulter' : (status === 'scheduled' ? 'assigned' : status);
                        const statusColor = isDefaulter ? '#DC2626' : status === 'completed' ? '#10B981' : status === 'results_published' ? '#8B5CF6' : status === 'ongoing' ? '#2563EB' : '#F59E0B';
                        const displayName = getCandidateDisplayName(c);
                        const initials = getCandidateInitials(c);
                        return (
                          <tr key={c.id} style={{ borderBottom: i < filteredExamDetailCandidates.length - 1 ? '1px solid #F1F5F9' : 'none', background: i % 2 === 0 ? '#fff' : '#FAFAFA' }}>
                            <td style={{ padding: '12px 16px', overflow: 'hidden' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ width: '30px', height: '30px', borderRadius: '50%', background: '#EFF6FF', color: '#2563EB', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 700, flexShrink: 0 }}>{initials}</span>
                                <span title={displayName} style={{ fontWeight: 600, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayName}</span>
                              </div>
                            </td>
                            <td title={c.email} style={{ padding: '12px 16px', color: '#6B7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.email}</td>
                            <td title={c.college_name || c.college || '—'} style={{ padding: '12px 16px', color: '#6B7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.college_name || c.college || '—'}</td>
                            <td title={c.degree || '—'} style={{ padding: '12px 16px', color: '#6B7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.degree || '—'}</td>
                            <td title={c.skill || '—'} style={{ padding: '12px 16px', color: '#6B7280' }}>{c.skill || '—'}</td>
                            <td style={{ padding: '12px 16px' }}>
                              <span style={{ background: `${statusColor}18`, color: statusColor, borderRadius: '20px', padding: '3px 10px', fontSize: '11px', fontWeight: 600, textTransform: 'capitalize', whiteSpace: 'nowrap' }}>{statusLabel}</span>
                            </td>
                            <td style={{ padding: '12px 16px', fontWeight: 600, color: '#1E293B' }}>{c.percentage != null ? `${c.percentage}%` : '—'}</td>
                            <td style={{ padding: '12px 16px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                                <button
                                  type="button"
                                  title="View candidate"
                                  onClick={() => handleOpenCandidateDetail(c)}
                                  style={{ width: '30px', height: '30px', border: '1px solid #BFD6FF', borderRadius: '8px', background: '#FFFFFF', color: '#2563EB', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                                >
                                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>
                                </button>
                                <button
                                  type="button"
                                  title="Unassign candidate"
                                  onClick={() => setCandidateToUnassign(c)}
                                  style={{ width: '30px', height: '30px', border: '1px solid #FECACA', borderRadius: '8px', background: '#FFFFFF', color: '#DC2626', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                                >
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M8 6V4h8v2"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
        );
      })()}

      {candidateDetail && (
        <div className="emx-modal-overlay" onClick={() => setCandidateDetail(null)}>
          <div onClick={e => e.stopPropagation()} style={{
            background: '#fff', borderRadius: '16px', width: 'min(92vw, 1420px)',
            height: '90vh', maxHeight: '90vh', display: 'flex', flexDirection: 'column',
            boxShadow: '0 24px 64px rgba(15,23,42,0.22)', overflow: 'hidden',
          }}>
            <div style={{ padding: '24px 28px 16px', borderBottom: '1px solid #F1F5F9', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#111827' }}>{candidateDetail.name}</h2>
                <p style={{ margin: '5px 0 0', fontSize: '13px', color: '#64748B' }}>Candidate attempt details</p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <button
                  type="button"
                  onClick={handleExportCandidateCsv}
                  style={{ height: '34px', padding: '0 14px', border: '1px solid #BFD6FF', borderRadius: '999px', background: '#FFFFFF', color: '#2563EB', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}
                >
                  Export CSV
                </button>
                <button onClick={() => setCandidateDetail(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', fontSize: '22px', lineHeight: 1, padding: '2px 6px' }}>×</button>
              </div>
            </div>

            <div style={{ padding: '16px 28px', display: 'grid', gridTemplateColumns: 'repeat(9, minmax(0, 1fr))', gap: '10px', borderBottom: '1px solid #F1F5F9', flexShrink: 0 }}>
              {[
                { label: 'Email', value: candidateDetail.email || '—' },
                { label: 'College', value: candidateDetail.college_name || candidateDetail.college || '—' },
                { label: 'Degree', value: candidateDetail.degree || '—' },
                { label: 'Exam Attempted', value: candidateDetail.examTitle || '—' },
                { label: 'Status', value: candidateDetail.submit_reason === 'tab_switch' ? 'Defaulter' : (candidateDetail.assignment_status || '—') },
                { label: 'Score', value: candidateDetail.percentage != null ? `${candidateDetail.percentage}%` : '—' },
                { label: 'Submitted On', value: candidateDetail.submitted_at ? new Date(candidateDetail.submitted_at).toLocaleString('en-IN') : '—' },
                { label: 'Violation', value: formatViolationLabel(candidateDetail.submit_reason, candidateDetail.tab_switch_count) },
                { label: 'Tab Switches', value: candidateDetail.tab_switch_count ?? 0 },
              ].map(({ label, value }) => (
                <div key={label} style={{ background: '#F8FAFC', borderRadius: '8px', padding: '10px', minWidth: 0 }}>
                  <div style={{ fontSize: '10px', color: '#94A3B8', fontWeight: 700, textTransform: 'uppercase', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</div>
                  <div title={String(value)} style={{ fontSize: '12px', fontWeight: 600, color: '#1E293B', marginTop: '5px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value}</div>
                </div>
              ))}
            </div>

            <div style={{ padding: '18px 28px 24px', display: 'grid', gridTemplateRows: 'minmax(0, 1fr) minmax(150px, 0.25fr)', gap: '18px', flex: 1, minHeight: 0, overflow: 'hidden' }}>
              <div style={{ minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px', marginBottom: '12px' }}>
                  <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 700, color: '#374151' }}>Question Answers</h3>
                  {candidateSectionScores.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: '8px', maxWidth: '72%' }}>
                      {candidateSectionScores.map(section => (
                        <span key={section.name} style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', minHeight: '30px', padding: '5px 10px', borderRadius: '999px', background: '#EFF6FF', border: '1px solid #BFD6FF', color: '#1E3A8A', fontSize: '12px', fontWeight: 700 }}>
                          <span style={{ color: '#475569', fontWeight: 600 }}>{section.name}</span>
                          <span>{section.score}/{section.total}</span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                {candidateAnswersLoading ? (
                  <p style={{ color: '#94A3B8', fontSize: '13px' }}>Loading answers...</p>
                ) : candidateAnswers.length === 0 ? (
                  <p style={{ color: '#94A3B8', fontSize: '13px' }}>No question answers found for this candidate.</p>
                ) : (
                  <div style={{ border: '1px solid #E5E7EB', borderRadius: '12px', overflow: 'hidden', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', tableLayout: 'fixed' }}>
                        <colgroup>
                          <col style={{ width: '48%' }} />
                          <col style={{ width: '18%' }} />
                          <col style={{ width: '18%' }} />
                          <col style={{ width: '16%' }} />
                        </colgroup>
                        <thead>
                          <tr style={{ background: '#F8FAFC', position: 'sticky', top: 0, zIndex: 1 }}>
                            {['Question', 'Selected', 'Correct', 'Result'].map(h => (
                              <th key={h} style={{ padding: '11px 14px', textAlign: 'left', fontWeight: 700, color: '#64748B', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #E5E7EB' }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {visibleCandidateAnswers.map((answer, index) => {
                            const resultLabel = answer.is_correct === true ? 'Correct' : answer.is_correct === false ? 'Incorrect' : 'Not Answered';
                            const resultColor = answer.is_correct === true ? '#059669' : answer.is_correct === false ? '#DC2626' : '#64748B';
                            return (
                              <tr key={`${answer.question_id}-${index}`} style={{ borderBottom: index < visibleCandidateAnswers.length - 1 ? '1px solid #F1F5F9' : 'none' }}>
                                <td title={answer.question_text || '—'} style={{ padding: '12px 14px', color: '#1E293B', lineHeight: 1.35, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{answer.question_text || '—'}</td>
                                <td title={answer.selected_option || '—'} style={{ padding: '12px 14px', color: '#64748B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{answer.selected_option || '—'}</td>
                                <td title={answer.correct_option || '—'} style={{ padding: '12px 14px', color: '#64748B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{answer.correct_option || '—'}</td>
                                <td style={{ padding: '12px 14px' }}>
                                  <span style={{ background: `${resultColor}18`, color: resultColor, borderRadius: '999px', padding: '4px 10px', fontSize: '11px', fontWeight: 700, whiteSpace: 'nowrap' }}>{resultLabel}</span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {false && <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '14px', padding: '10px 12px', borderTop: '1px solid #E5E7EB', background: '#FFFFFF' }}>
                      <button type="button" disabled={candidateAnswerPage === 1} onClick={() => setCandidateAnswerPage(p => Math.max(1, p - 1))} style={{ border: 'none', background: 'transparent', color: candidateAnswerPage === 1 ? '#BFDBFE' : '#2563EB', fontSize: '12px', cursor: candidateAnswerPage === 1 ? 'default' : 'pointer' }}>Prev</button>
                      <span style={{ fontSize: '12px', color: '#64748B' }}>{candidateAnswerPage} / {answerTotalPages}</span>
                      <button type="button" disabled={candidateAnswerPage === answerTotalPages} onClick={() => setCandidateAnswerPage(p => Math.min(answerTotalPages, p + 1))} style={{ border: 'none', background: 'transparent', color: candidateAnswerPage === answerTotalPages ? '#BFDBFE' : '#2563EB', fontSize: '12px', cursor: candidateAnswerPage === answerTotalPages ? 'default' : 'pointer' }}>Next</button>
                    </div>}
                  </div>
                )}
              </div>

              <div style={{ minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <h3 style={{ margin: '0 0 12px', fontSize: '15px', fontWeight: 700, color: '#374151' }}>Screenshots</h3>
              {candidateEvidenceLoading ? (
                <p style={{ color: '#94A3B8', fontSize: '13px' }}>Loading screenshots…</p>
              ) : candidateDetail.screenshots?.length ? (
                <div style={{ display: 'grid', gap: '10px', overflowY: 'auto', minHeight: 0 }}>
                  {candidateDetail.screenshots.map((filename) => (
                    <button
                      key={filename}
                      type="button"
                      onClick={() => handleOpenScreenshot(filename)}
                      style={{ display: 'flex', width: '100%', background: '#fff', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', border: '1px solid #E5E7EB', borderRadius: '10px', textDecoration: 'none', color: '#2563EB', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
                    >
                      <span title={filename} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{filename}</span>
                      <span>Open</span>
                    </button>
                  ))}
                </div>
              ) : (
                <p style={{ color: '#94A3B8', fontSize: '13px' }}>No screenshots found for this candidate.</p>
              )}
              </div>
            </div>
          </div>
        </div>
      )}

      {candidateToUnassign && (
        <div className="em-confirm-overlay em-confirm-overlay--front" onClick={() => setCandidateToUnassign(null)}>
          <div className="em-confirm-dialog" onClick={e => e.stopPropagation()}>
            <button className="em-confirm-close" onClick={() => setCandidateToUnassign(null)}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M12 4L4 12M4 4L12 12" stroke="#64748B" strokeWidth="1.8" strokeLinecap="round"/></svg>
            </button>
            <div className="em-confirm-icon">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M8 6V4h8v2"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>
            </div>
            <div className="em-confirm-copy">
              <h2>Unassign this candidate?</h2>
              <p>{candidateToUnassign.name || candidateToUnassign.email} will be removed from this exam assignment.</p>
            </div>
            <div className="em-confirm-actions">
              <button className="em-confirm-btn em-confirm-cancel" onClick={() => setCandidateToUnassign(null)}>Cancel</button>
              <button className="em-confirm-btn em-confirm-delete" onClick={handleUnassignCandidate}>Yes, Unassign</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ExamManagement;

